import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  Typography,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Chip,
  Paper,
  Stack,
  Divider,
  Avatar,
  Collapse,
  Alert,
  Grid,
  alpha,
  TextField,
  MenuItem,
  CircularProgress,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { normalizePromoStoreImageSelection } from "../EditProduct/asiImageUtils";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import BrushOutlinedIcon from "@mui/icons-material/BrushOutlined";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import MiscellaneousServicesOutlinedIcon from "@mui/icons-material/MiscellaneousServicesOutlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import TagIcon from "@mui/icons-material/Tag";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import HistoryIcon from "@mui/icons-material/History";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";

import { copyOrderItem, deleteOrderItem, updateOrderItems, rehydrateMissingCustomOrderItemSupplier } from "../../services/orderItem";
import { recoverMissingCustomSupplierOnSalesOrderEdit } from "./salesOrderEditSupplierRecovery";
import { clonePromoStoreProduct } from "./promoStoreCopy";
import { deletePromoStoreItem } from "../../services/promoStoreItems";
import {
  createOrderCartSection,
  updateOrderCartSection,
  deleteOrderCartSection,
} from "../../services/orders";
import EditProduct from "../EditProduct";
import {
  applyDecorationCopyToProduct,
  resolveMatrixChargeTypesForCopy,
  getCartProductCopyKey,
} from "../EditProduct/Decoration/copyHelpers";
import { getSingleDecoratorDetails } from "../../services/decorator";
import OtherServiceModal from "../otherServices";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { ToastContainer, toast } from "react-toastify";
import { getSingleOtherService } from "../../services/orderItem";
import { deleteOtherService } from "../../services/orderItem";
import {
  parseOrderProductId,
  CUSTOM_PREFIX,
  isCustomProduct,
} from "../../services/products";
import {
  computeTotalsFromOrder,
  fromCents,
  toCents,
  normalizeProductTaxIds,
  resolveProductTaxes,
  isPersistedTaxBreakdownCurrent,
  buildResolvedTaxBreakdown,
} from "../../utils/totals";
import { sumOtherServiceCustomerAmounts } from "../../utils/otherServicePricing";
import { resolveCartSummaryTaxCents } from "./cartSummaryTax";
import {
  computeOrderItemPricingSnapshot,
  resolveAdditionalCostFlags,
} from "../../utils/pricingEngine";
import {
  buildProductWorkspaceIdentity,
  resolvePersistedOrderItemId,
  resolveSourceOrderItemId,
  shouldBlockProtectedOrderItemDeletion,
  resolvePersistedOrderItemCopyId,
  resolvePersistedOrderItemDeleteId,
} from "./identity";
import { buildCartLayout, buildProductLayoutRows, CART_LAYOUT_ROW_TYPES, normalizeSectionStyle, normalizeSectionTitle } from "../../pages/instances/sales/orders/orders-single/cartLayout";

import { ShowcaseProductCard, ShowcaseProductLayout, ShowcaseProductViewSelector, useShowcaseProductView } from "./ShowcaseProductViews";
import ProductPopup from "../../pages/instances/connect/products/ProductPopup";

import { showcaseEditedProduct } from "../../pages/instances/dashboard/promoshowcase/useShowcaseProductPage";

const formatMoney = (v) => `$${(Number(v) || 0).toFixed(2)}`;

export const commitWorkspaceMutation = async ({
  source,
  mutationType,
  mutate,
  metadata = {},
  onWorkspaceMutationCommitted,
}) => {
  const mutationResult = mutate();
  if (!mutationResult?.ok) return mutationResult;

  if (typeof onWorkspaceMutationCommitted === "function") {
    await onWorkspaceMutationCommitted({
      source,
      mutationType,
      nextPayload: mutationResult.payload ?? null,
      metadata,
    });
  }
  return mutationResult;
};
const formatPct = (v) => `${(Number(v) || 0).toFixed(2)}%`;
const unitsLabel = (q) =>
  `${Number(q) === 1 ? "1 unit" : `${Number(q) || 0} units`}`;
const PROTECTED_ORDER_ITEM_DELETE_MESSAGE =
  "This item is included in an active Sales Order and cannot be deleted from the order cart. Update or remove the Sales Order first.";

const firstPresentString = (...values) => {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
};

function extractKey(units, key) {
  return units?.map((unit) => ({ [key]: unit[key] }));
}

const UNIT = "unit";
const MARGIN = "margin";
const AMOUNT = "amount";

const TYPE_META = {
  Shipping: {
    label: "Shipping",
    Icon: LocalShippingOutlinedIcon,
    chipSx: {
      bgcolor: "rgba(13,94,166,0.10)",
      color: "#0D5EA6",
      borderColor: "#0D5EA6",
    },
  },
  Art: {
    label: "Art",
    Icon: BrushOutlinedIcon,
    chipSx: {
      bgcolor: "rgba(195,23,29,0.10)",
      color: "#c3171d",
      borderColor: "#c3171d",
    },
  },
  Other: {
    label: "Other",
    Icon: MoreHorizOutlinedIcon,
    chipSx: {
      bgcolor: "rgba(99,115,129,0.12)",
      color: "text.secondary",
      borderColor: "divider",
    },
  },
};

const renderTypeBadge = (type) => {
  const key = type || "Other";
  const meta = TYPE_META[key] || TYPE_META.Other;
  const Icon = typeof meta?.Icon === "function" ? meta.Icon : null;

  return (
    <Chip
      size="small"
      variant="outlined"
      icon={Icon ? <Icon sx={{ fontSize: "16px !important" }} /> : undefined}
      label={meta.label}
      sx={{
        ...meta.chipSx,
        fontWeight: 700,
        height: 26,
        borderRadius: 1,
        "& .MuiChip-icon": { ml: 0.75 },
      }}
    />
  );
};

const mapToDataRows = (items, fallbackTitle = "Item", unitOverride = null) =>
  (items || []).map((item) => ({
    title: item?.CostName || item?.SubType || fallbackTitle,
    unit: item?.Quantity ?? 0,
    margin: item?.margin != null ? item.margin : item?.Margin || 0,
    amount:
      fallbackTitle === "Artwork"
        ? item.Charge * unitOverride
        : item?.Retail * item?.Quantity || 0,
  }));

const decorationToDataRows = (items, fallbackTitle = "Item") =>
  (items || []).flatMap((item) =>
    (item?.Charge || []).map((charge) => ({
      title: charge?.Name || charge?.SubType || fallbackTitle,
      unit: charge?.Quantity ?? 0,
      margin: charge?.Margin || 0,
      amount: charge?.Retail * charge?.Quantity || 0,
    })),
  );


const getEffectiveMarginCost = (row = {}) => {
  const converted = Number(row?.ConvertedNetCost ?? row?.convertedNetCost);
  if (Number.isFinite(converted) && converted > 0) return converted;
  const net = Number(row?.NetCost ?? row?.netCost ?? row?.Cost ?? row?.cost);
  return Number.isFinite(net) ? net : 0;
};

const getOriginalSourceNetCost = (row = {}) => {
  const existingSource = Number(row?.sourceNetCost);
  if (Number.isFinite(existingSource) && existingSource > 0) return existingSource;

  const converted = Number(row?.ConvertedNetCost ?? row?.convertedNetCost);
  const candidates = [row?.NetCost, row?.netCost, row?.Cost, row?.cost];

  for (const value of candidates) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) continue;

    if (
      Number.isFinite(converted) &&
      converted > 0 &&
      Math.abs(numeric - converted) < 0.000001
    ) {
      continue;
    }

    return numeric;
  }

  return null;
};

const shapePricingRowForCartSummary = (row = {}) => {
  const converted = Number(row?.ConvertedNetCost ?? row?.convertedNetCost);
  const hasConvertedCost = Number.isFinite(converted) && converted > 0;
  const effectiveCost = getEffectiveMarginCost(row);
  const sourceNetCost = getOriginalSourceNetCost(row);

  return {
    ...row,
    NetCost: effectiveCost,
    netCost: effectiveCost,
    ...(hasConvertedCost && sourceNetCost != null ? { sourceNetCost } : {}),
  };
};

const shapeDecorationsForCartSummary = (decorations = []) => (
  (Array.isArray(decorations) ? decorations : []).map((decoration) => {
    const chargeValue = decoration?.Charge ?? decoration?.charge;
    const shapedCharges = Array.isArray(chargeValue)
      ? chargeValue.map((charge) => shapePricingRowForCartSummary(charge))
      : chargeValue;

    return {
      ...decoration,
      ...(Object.prototype.hasOwnProperty.call(decoration, "Charge") ? { Charge: shapedCharges } : {}),
      ...(Object.prototype.hasOwnProperty.call(decoration, "charge") ? { charge: shapedCharges } : {}),
    };
  })
);

const shapeProductForCartSummary = (product = {}) => {
  const pricings = Array.isArray(product?.pricings)
    ? product.pricings.map((row) => shapePricingRowForCartSummary(row))
    : product?.pricings;
  const pricing = Array.isArray(product?.Pricing)
    ? product.Pricing.map((row) => shapePricingRowForCartSummary(row))
    : product?.Pricing;

  return {
    ...product,
    ...(Array.isArray(product?.pricings) ? { pricings } : {}),
    ...(Array.isArray(product?.Pricing) ? { Pricing: pricing } : {}),
    ...(Array.isArray(product?.decorations) ? { decorations: shapeDecorationsForCartSummary(product.decorations) } : {}),
    ...(Array.isArray(product?.Decorations) ? { Decorations: shapeDecorationsForCartSummary(product.Decorations) } : {}),
  };
};

export const buildPricingSnapshotFromProduct = (product = {}) => {
  const rawPricings =
    Array.isArray(product?.pricings) && product.pricings.length
      ? product.pricings
      : [
          {
            Quantity: product?.Quantity ?? 1,
            NetCost: product?.NetCost ?? product?.Cost ?? 0,
            ConvertedNetCost: product?.ConvertedNetCost ?? product?.convertedNetCost ?? null,
            Retail: product?.Retail ?? 0,
            Margin: product?.Margin ?? 40,
            IsLocked: Boolean(product?.IsLocked),
          },
        ];

  const pricingRows = rawPricings.map((row) => ({
    priceType: "Breakdown",
    quantity: Number(row?.Quantity ?? row?.quantity ?? 0) || 0,
    netCost:
      getEffectiveMarginCost(row),
    retail:
      Number(
        row?.Retail ??
          row?.retail ??
          row?.ClientPrice ??
          row?.clientPrice ??
          0,
      ) || 0,
    clientPrice:
      Number(
        row?.ClientPrice ??
          row?.clientPrice ??
          row?.Retail ??
          row?.retail ??
          0,
      ) || 0,
    margin: row?.Margin ?? row?.margin,
    isLocked: Boolean(row?.IsLocked ?? row?.isLocked),
    isRetailLocked: Boolean(row?.IsRetailLocked ?? row?.isRetailLocked),
  }));

  const decorations = (product?.decorations || []).map((dec) => ({
    rows: (dec?.Charge || []).map((charge) => ({
      quantity: Number(charge?.Quantity ?? charge?.quantity ?? 0) || 0,
      netCost:
        getEffectiveMarginCost(charge),
      retail:
        Number(
          charge?.Retail ??
            charge?.retail ??
            charge?.ClientPrice ??
            charge?.clientPrice ??
            0,
        ) || 0,
      clientPrice:
        Number(
          charge?.ClientPrice ??
            charge?.clientPrice ??
            charge?.Retail ??
            charge?.retail ??
            0,
        ) || 0,
      margin: charge?.Margin ?? charge?.margin,
      includeInPrice: charge?.includeInPrice,
      subtractFromMargin: charge?.subtractFromMargin,
      displayToClient: charge?.displayToClient,
      pricingOption: charge?.pricingOption,
      DisplayStatus: charge?.DisplayStatus,
      displayStatus: charge?.displayStatus,
      costType: charge?.costType,
      priceType: charge?.priceType ?? charge?.PriceType,
      chargeName: charge?.chargeName ?? charge?.ChargeName ?? charge?.Name,
      CurrencyCode: charge?.CurrencyCode ?? charge?.currencyCode ?? null,
      PricingCurrencyCode: charge?.PricingCurrencyCode ?? charge?.pricingCurrencyCode ?? null,
      NetCost: charge?.NetCost ?? charge?.netCost ?? null,
      ConvertedNetCost: charge?.ConvertedNetCost ?? charge?.convertedNetCost ?? null,
      ExchangeRate: charge?.ExchangeRate ?? charge?.exchangeRate ?? null,
      convertedCurrency: charge?.convertedCurrency ?? null,
      decoratorId: charge?.decoratorId ?? charge?.DecoratorId ?? dec?.DecoratorDetails?.decoratorId ?? null,
      supplierId: charge?.supplierId ?? charge?.SupplierId ?? null,
      addressId: charge?.addressId ?? charge?.AddressId ?? null,
      decorationId:
        charge?.decorationId ??
        charge?.DecorationId ??
        dec?.DecoratorDetails?.DecoratorID,
      sharedGroupKey:
        charge?.sharedGroupKey ?? dec?.DecoratorDetails?.sharedGroupKey,
      sharedQuantityMode:
        charge?.sharedQuantityMode ??
        dec?.DecoratorDetails?.sharedQuantityMode,
      sharedTotalQuantity:
        charge?.sharedTotalQuantity ??
        dec?.DecoratorDetails?.sharedTotalQuantity,
    })),
  }));

  return computeOrderItemPricingSnapshot({
    pricingRows,
    decorations,
    selectedTaxRate:
      Number(product?.taxDetails?.rate ?? product?.taxDetails?.Rate ?? 0) || 0,
  });
};

const decodePromoStoreText = (value = "") => String(value || "")
  .replace(/&#(x?[0-9a-f]+);/gi, (match, code) => {
    const isHex = String(code).toLowerCase().startsWith("x");
    const numeric = parseInt(isHex ? code.slice(1) : code, isHex ? 16 : 10);
    return Number.isFinite(numeric) ? String.fromCodePoint(numeric) : match;
  })
  .replace(/&quot;/g, '"')
  .replace(/&#039;|&apos;/g, "'")
  .replace(/&amp;/g, "&")
  .replace(/&lt;/g, "<")
  .replace(/&gt;/g, ">");

const promoStoreVariationTitle = (pricing = {}) => {
  const costName = decodePromoStoreText(pricing?.CostName || pricing?.costName || "").trim();
  if (!costName.startsWith("PromoStore Variation:")) {
    return { title: "", sharedDecoration: "" };
  }

  const body = costName.replace(/^PromoStore Variation:\s*/, "");
  const [title, sharedDecoration = ""] = body.split(/\nShared Decoration:\n/);
  return { title: decodePromoStoreText(title).trim(), sharedDecoration: decodePromoStoreText(sharedDecoration).trim() };
};

const pricingRowOptionLabel = (pricing = {}) => {
  const optionValues = [
    firstPresentString(pricing?.Color, pricing?.color, pricing?.selectedColor),
    firstPresentString(pricing?.Size, pricing?.size, pricing?.selectedSize),
  ].filter(Boolean);

  return optionValues.join(" - ");
};

export const transformData = (apiProducts, order = {}, source = "order-cart") => {
  const pickNum = (...vals) => {
    for (const v of vals) {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
    return 0;
  };

  const pickPositive = (...vals) => {
    for (const v of vals) {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return 0;
  };

  const normLine = (p) => {
    const qty = Number(p?.Quantity ?? p?.quantity) || 0;
    const cost = getEffectiveMarginCost(p);
    const costC = toCents(cost);
    const retailRawBase = pickPositive(p?.Retail, p?.retail);
    const clientRaw = pickPositive(
      p?.ClientPrice,
      p?.clientPrice,
      p?.Price,
      p?.price,
    );

    const retailOnlyLocked = [p?.IsRetailLocked, p?.isRetailLocked]
      .some((value) => value === true || String(value).toLowerCase() === "true");
    const locked = retailOnlyLocked || Boolean(p?.IsLocked ?? p?.isLocked);
    const hasSavedRetail = (p?.Retail != null && p?.Retail !== "") ||
      (p?.retail != null && p?.retail !== "");
    const retailRaw = retailOnlyLocked && hasSavedRetail
      ? pickNum(p?.Retail ?? p?.retail)
      : clientRaw > 0 &&
        (retailRawBase <= 0 || Math.abs(retailRawBase - cost) <= 0.01)
          ? clientRaw
          : retailRawBase > 0
            ? retailRawBase
            : clientRaw;

    const hasRetail = retailRaw > 0;
    const marginPct = Number(p?.Margin ?? p?.margin);
    const canDerive =
      !locked &&
      Number.isFinite(marginPct) &&
      marginPct > 0 &&
      marginPct < 100 &&
      cost > 0;

    let retailC;

    if (canDerive) {
      retailC = toCents(Number((cost / (1 - marginPct / 100)).toFixed(2)));
    } else if (locked || hasRetail) {
      retailC = toCents(retailRaw);
    } else {
      const m = Number(p?.Margin ?? p?.margin);
      retailC =
        Number.isFinite(m) && m < 100
          ? Math.round(costC / (1 - m / 100))
          : toCents(retailRaw || 0);
    }

    const quotedMarginPct = Number.isFinite(Number(p?.Margin ?? p?.margin))
      ? Number(p?.Margin ?? p?.margin)
      : null;

    return { qty, costC, retailC, quotedMarginPct, locked };
  };

  return (apiProducts || []).map((product) => {
    const resolvedTaxes = resolveProductTaxes(product, order);

    const rawPricings =
      Array.isArray(product?.pricings) && product.pricings.length
        ? product.pricings
        : [
            {
              Quantity: product?.Quantity ?? 1,
              NetCost: product?.NetCost ?? product?.Cost ?? 0,
            ConvertedNetCost: product?.ConvertedNetCost ?? product?.convertedNetCost ?? null,
              Retail: product?.Retail ?? 0,
              Margin: product?.Margin ?? 40,
              IsLocked: Boolean(product?.IsLocked),
              },
          ];

    let subC = 0;
    let taxC = 0;
    let bookedMarginC = 0;
    let totalUnits = 0;
    const rows = [];
    let promoStoreSharedDecoration = "";

    rawPricings.forEach((p) => {
      const n = normLine(p);
      const extC = n.retailC * n.qty;
      const lineTaxC = resolvedTaxes.reduce(
        (sum, tax) => sum + Math.round(extC * (Number(tax?.rate) || 0)),
        0,
      );

      subC += extC;
      taxC += lineTaxC;
      totalUnits += n.qty;

      const displayMarginPct = Number.isFinite(n.quotedMarginPct)
        ? n.quotedMarginPct
        : n.retailC
          ? +(((n.retailC - n.costC) / n.retailC) * 100).toFixed(2)
          : 0;

      bookedMarginC += (n.retailC - n.costC) * n.qty;

      const promoStoreVariation = promoStoreVariationTitle(p);
      if (promoStoreVariation.sharedDecoration && !promoStoreSharedDecoration) {
        promoStoreSharedDecoration = promoStoreVariation.sharedDecoration;
      }

      const pricingSku = firstPresentString(p?.SKU, p?.sku);
      const optionLabel = pricingRowOptionLabel(p);
      const defaultTitle = [
        pricingSku ? `SKU ${pricingSku}` : "",
        optionLabel,
      ].filter(Boolean).join(" - ");

      rows.push({
        title:
          promoStoreVariation.title ||
          defaultTitle ||
          "Price Break",
        unit: n.qty,
        margin: displayMarginPct,
        amount: fromCents(extC),
      });
    });

    (product?.decorations || []).forEach((dec) => {
      (dec?.Charge || []).forEach((ch) => {
        const flags = resolveAdditionalCostFlags(ch);
        // Bundled prices are represented by the product selling subtotal,
        // not as a separate charge. Margin-only/hidden rows are not revenue.
        if (flags?.includeInPrice || flags?.subtractFromMargin || !flags?.displayToClient) return;

        const q = Number(ch?.Quantity) || 0;
        const costRaw = getEffectiveMarginCost(ch);
        const retailRawBase = pickPositive(ch?.Retail, ch?.retail);
        const clientRaw = pickPositive(
          ch?.ClientPrice,
          ch?.clientPrice,
          ch?.Price,
          ch?.price,
        );

        const resolvedRetailRaw =
          clientRaw > 0 &&
          (retailRawBase <= 0 || Math.abs(retailRawBase - costRaw) <= 0.01)
            ? clientRaw
            : retailRawBase > 0
              ? retailRawBase
              : clientRaw;

        const rowMarginPct = Number(ch?.Margin ?? ch?.margin);
        const canDeriveFromM =
          Number.isFinite(rowMarginPct) &&
          rowMarginPct > 0 &&
          rowMarginPct < 100 &&
          costRaw > 0;

        const rC = canDeriveFromM
          ? toCents(Number((costRaw / (1 - rowMarginPct / 100)).toFixed(2)))
          : toCents(resolvedRetailRaw);
        const cC = toCents(costRaw);
        const extC = rC * q;

        const lineTaxC = resolvedTaxes.reduce(
          (sum, tax) => sum + Math.round(extC * (Number(tax?.rate) || 0)),
          0,
        );

        subC += extC;
        taxC += lineTaxC;

        const quotedDecMarginPct = Number.isFinite(Number(ch?.Margin))
          ? Number(ch.Margin)
          : null;
        const displayDecMarginPct = Number.isFinite(quotedDecMarginPct)
          ? quotedDecMarginPct
          : rC
            ? +(((rC - cC) / rC) * 100).toFixed(2)
            : 0;

        bookedMarginC += (rC - cC) * q;

        const isPromoStoreSharedDecoration = Boolean(ch?.isPromoStoreSharedDecoration);

        const decorationDetails = [
          firstPresentString(dec?.Location, dec?.location)
            ? `Location: ${firstPresentString(dec?.Location, dec?.location)}`
            : "",
          dec?.ArtWork || dec?.artwork ? "Artwork attached" : "",
        ].filter(Boolean);

        rows.push({
          title: isPromoStoreSharedDecoration ? "Shared Decoration" : (ch?.Name || ch?.SubType || "Decoration"),
          description: isPromoStoreSharedDecoration
            ? decodePromoStoreText(ch?.Description || ch?.description || "")
            : decorationDetails.join(" · "),
          unit: q,
          margin: displayDecMarginPct,
          amount: fromCents(extC),
          _isDecoration: true,
          _isPromoStoreSharedDecoration: isPromoStoreSharedDecoration,
        });
      });
    });

    const pricingSnapshot = buildPricingSnapshotFromProduct(product);
    // Include in Price is already part of the customer selling subtotal.
    // For a taxable product, GST applies to that entire selling price; the
    // mode controls presentation, not tax exemption.
    const nonTaxable = product?.taxable === false || product?.isTaxable === false ||
      product?.taxDetails?.taxable === false ||
      ['exempt', 'non-taxable'].includes(String(product?.taxTreatment || '').trim().toLowerCase());
    const taxableAmount = nonTaxable
      ? 0
      : fromCents(toCents(pricingSnapshot?.subtotal ?? fromCents(subC)));
    const persistedTaxBreakdown = Array.isArray(product?.taxBreakdown)
      ? product.taxBreakdown
      : (Array.isArray(product?.totals?.taxBreakdown) ? product.totals.taxBreakdown : []);
    const taxBreakdown = isPersistedTaxBreakdownCurrent(
      persistedTaxBreakdown,
      resolvedTaxes,
      taxableAmount
    )
      ? persistedTaxBreakdown.map((tax) => ({
          ...tax,
          taxableAmount,
          taxAmount: Number(tax?.taxAmount ?? tax?.amount) || 0,
        }))
      : buildResolvedTaxBreakdown(resolvedTaxes, taxableAmount);

    const aggregateTaxAmount = +taxBreakdown
      .reduce((sum, tax) => sum + (Number(tax.taxAmount) || 0), 0)
      .toFixed(2);

    const totalRow = {
      title: "Total",
      unit: totalUnits,
      margin: Number(pricingSnapshot?.marginPercentage || 0),
      amount: +((Number(pricingSnapshot?.subtotal) || 0) + aggregateTaxAmount).toFixed(2),
      taxAmount: aggregateTaxAmount,
      taxBreakdown,
      _isTotal: true,
    };

    rows.push({ title: "Total", ...totalRow });

    const { id: rawId } = parseOrderProductId(product?.ProductId || "");
    const custom = isCustomProduct(product);
    const catalogSource = String(
      product?.Source ??
      product?.source ??
      product?.LookupType ??
      product?.lookupType ??
      ""
    ).trim().toLowerCase();
    const catalogSourceLabel = catalogSource.includes("promoworld")
      ? "PromoWorld"
      : "ASI";
    const productIdDisplay = custom
      ? `${CUSTOM_PREFIX}${rawId}`
      : `${rawId} (${catalogSourceLabel})`;
    const persistedOrderItemId = resolvePersistedOrderItemId(product, { source });
    const sourceOrderItemId = resolveSourceOrderItemId(product, { source });

    return {
      itemId: product?.cartLineId || product?.id,
      cartLineId: product?.cartLineId ?? null,
      dbItemId: persistedOrderItemId,
      orderItemId: persistedOrderItemId,
      persistedOrderItemId,
      sourceOrderItemId,
      sortOrder: product?.sortOrder ?? product?.SortOrder ?? null,
      productId: product.ProductId,
      productIdDisplay,
      sku: product.SKU || product.Sku || product.sku || product.Number || product.number || "",
      supplier: `${
        product?.SupplierName ||
        product?.supplierName ||
        product?.Supplier?.Name ||
        product?.supplier?.name ||
        (typeof product?.Supplier === "string" ? product.Supplier : "") ||
        "—"
      } (${product?.CurrencyCode || product?.currencyCode || "—"})`,
      pricingCurrencyCode:
        product?.PricingCurrencyCode ||
        product?.pricingCurrencyCode,
      showcaseIdentity: product.showcaseIdentity,
      name: product.Name,
      image: product.ImageUrl,
      description: product.Description,
      shortDescription: product.ShortDescription,
      categoryIds: Array.isArray(product?.categoryIds)
        ? product.categoryIds
        : Array.isArray(product?.CategoryIds)
          ? product.CategoryIds
          : Array.isArray(product?.categories)
            ? product.categories.map((category) => category?.id).filter(Boolean)
            : Array.isArray(product?.Categories)
              ? product.Categories.map((category) => category?.id).filter(Boolean)
              : [],
      categories: Array.isArray(product?.categories)
        ? product.categories
        : Array.isArray(product?.Categories)
          ? product.Categories
          : [],
      promoStoreSharedDecoration,
      data: rows,
      isTaxPresent:
        taxBreakdown.length > 0 ||
        aggregateTaxAmount > 0 ||
        normalizeProductTaxIds(product).length > 0,
    };
  });
};

export const resolveRenderableSortOrder = (value, fallbackIndex = 0) => {
  if (value === null || value === undefined || value === "") return fallbackIndex;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallbackIndex;
};

const marginColor = (pct) => {
  const n = Number(pct) || 0;
  if (n >= 40) return "success.main";
  if (n >= 20) return "warning.main";
  return "error.main";
};

const splitLabelValueText = (text = "") => {
  const source = String(text || "").trim();
  if (!source) return [];

  const labelPattern = /(?:^|\s)([A-Za-z][A-Za-z0-9/#&()+.-]*:)/g;
  const matches = [...source.matchAll(labelPattern)];

  if (!matches.length) return [{ label: "", value: source }];

  return matches.map((match, index) => {
    const label = match[1];
    const valueStart = match.index + match[0].length;
    const valueEnd = matches[index + 1]?.index ?? source.length;
    return {
      label,
      value: source.slice(valueStart, valueEnd).trim(),
    };
  });
};

const LabeledTextGroups = ({ text, labelSx = {}, valueSx = {}, groupSx = {} }) => {
  const groups = splitLabelValueText(text);

  if (!groups.length) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        flexWrap: "wrap",
        columnGap: 3,
        rowGap: 0.25,
      }}
    >
      {groups.map((group, index) => (
        <Box
          key={`${group.label || "text"}-${index}`}
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "baseline",
            minWidth: 0,
            ...groupSx,
          }}
        >
          {group.label && (
            <Box component="span" sx={{ fontWeight: 700, mr: 0.5, ...labelSx }}>
              {group.label}
            </Box>
          )}
          <Box component="span" sx={{ fontWeight: 400, ...valueSx }}>
            {group.value}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const PricingBreakdownTable = ({ row }) => {
  const sharedDecorationRow = (row.data || []).find((r) => r._isPromoStoreSharedDecoration);
  const variationRows = (row.data || []).filter((r) => !r._isTotal && !r._isDecoration && !r._isPromoStoreSharedDecoration);
  const decorationRows = (row.data || []).filter((r) => r._isDecoration && !r._isPromoStoreSharedDecoration);
  const totalRow = (row.data || []).find((r) => r.title === "Total");
  const isPromoStoreVariationBreakdown = Boolean(row.promoStoreSharedDecoration || sharedDecorationRow);

  const headerSx = {
    fontSize: "0.72rem",
    fontWeight: 700,
    color: "text.secondary",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    pb: 0.75,
    borderBottom: (theme) => `2px solid ${alpha(theme.palette.divider, 0.8)}`,
  };

  const cellSx = {
    fontSize: "0.82rem",
    py: 0.6,
    color: "text.primary",
  };

  return (
    <Box
      sx={{
        mt: 1.5,
        borderRadius: 1,
        overflow: "hidden",
        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.7)}`,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 90px 90px 100px",
          gap: 1,
          px: 1.5,
          pt: 1,
          pb: 0.5,
          bgcolor: (theme) =>
            alpha(
              theme.palette.grey[100],
              theme.palette.mode === "light" ? 1 : 0.06,
            ),
        }}
      >
        <Typography sx={headerSx}>
          Line Item / Variation
        </Typography>
        <Typography sx={{ ...headerSx, textAlign: "center" }}>
          Units
        </Typography>
        <Typography sx={{ ...headerSx, textAlign: "center" }}>
          Margin
        </Typography>
        <Typography sx={{ ...headerSx, textAlign: "right" }}>
          Amount
        </Typography>
      </Box>

      {variationRows.map((r, i) => (
        <Box
          key={i}
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 90px 90px 100px",
            gap: 1,
            px: 1.5,
            py: 0.5,
            bgcolor:
              i % 2 === 0
                ? "background.paper"
                : (theme) =>
                    alpha(
                      theme.palette.grey[50],
                      theme.palette.mode === "light" ? 1 : 0.04,
                    ),
            "&:hover": {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
            },
          }}
        >
          <Box sx={{ ...cellSx, whiteSpace: isPromoStoreVariationBreakdown ? "pre-line" : "normal", minWidth: 0 }}>
            <LabeledTextGroups text={r.title} />
          </Box>
          <Typography
            sx={{ ...cellSx, textAlign: "center", color: "text.secondary" }}
          >
            {r.unit} units
          </Typography>
          <Typography
            sx={{
              ...cellSx,
              textAlign: "center",
              color: marginColor(r.margin),
            }}
          >
            {formatPct(r.margin)}
          </Typography>
          <Typography
            sx={{
              ...cellSx,
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatMoney(r.amount)}
          </Typography>
        </Box>
      ))}

      {decorationRows.length > 0 && (
        <Box
          sx={{
            mt: 1,
            mx: 1,
            mb: 1,
            borderRadius: 1,
            overflow: "hidden",
            border: (theme) => `1px solid ${alpha(theme.palette.success.main, 0.16)}`,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 90px 90px 100px",
              gap: 1,
              px: 1.5,
              py: 0.7,
              bgcolor: (theme) => alpha(theme.palette.success.light, theme.palette.mode === "light" ? 0.18 : 0.08),
            }}
          >
            <Typography sx={headerSx}>Decoration</Typography>
            <Typography sx={{ ...headerSx, textAlign: "center" }}>Units</Typography>
            <Typography sx={{ ...headerSx, textAlign: "center" }}>Margin</Typography>
            <Typography sx={{ ...headerSx, textAlign: "right" }}>Amount</Typography>
          </Box>
          {decorationRows.map((r, i) => (
            <Box
              key={`decoration-${i}`}
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 90px 90px 100px",
                gap: 1,
                px: 1.5,
                py: 0.65,
                bgcolor: "background.paper",
                borderTop: i === 0 ? 0 : (theme) => `1px solid ${alpha(theme.palette.divider, 0.65)}`,
              }}
            >
              <Box>
                <Typography sx={{ ...cellSx, py: 0, fontWeight: 700 }}>{r.title}</Typography>
                {r.description && (
                  <Box sx={{ ...cellSx, py: 0.15, color: "text.secondary", fontSize: "0.76rem", minWidth: 0 }}>
                    <LabeledTextGroups
                      text={r.description}
                      labelSx={{ color: "text.secondary" }}
                      valueSx={{ color: "text.secondary" }}
                      groupSx={{ columnGap: 0 }}
                    />
                  </Box>
                )}
              </Box>
              <Typography sx={{ ...cellSx, textAlign: "center", color: "text.secondary" }}>{r.unit} units</Typography>
              <Typography sx={{ ...cellSx, textAlign: "center", color: marginColor(r.margin) }}>{formatPct(r.margin)}</Typography>
              <Typography sx={{ ...cellSx, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatMoney(r.amount)}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {sharedDecorationRow && (
        <>
          <Divider />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 90px 90px 100px",
              gap: 1,
              px: 1.5,
              py: 0.75,
              bgcolor: "background.paper",
            }}
          >
            <Box>
              <Typography sx={{ ...cellSx, fontWeight: 700 }}>
                Shared Decoration
              </Typography>
              <Typography
                sx={{
                  ...cellSx,
                  pt: 0,
                  color: "text.secondary",
                  whiteSpace: "pre-line",
                }}
              >
                {sharedDecorationRow.description || row.promoStoreSharedDecoration}
              </Typography>
            </Box>
            <Typography
              sx={{ ...cellSx, textAlign: "center", color: "text.secondary" }}
            >
              {sharedDecorationRow.unit} units
            </Typography>
            <Typography
              sx={{
                ...cellSx,
                textAlign: "center",
                color: marginColor(sharedDecorationRow.margin),
              }}
            >
              {formatPct(sharedDecorationRow.margin)}
            </Typography>
            <Typography
              sx={{
                ...cellSx,
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatMoney(sharedDecorationRow.amount)}
            </Typography>
          </Box>
        </>
      )}

      {totalRow && (
        <>
          <Divider />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 90px 90px 100px",
              gap: 1,
              mx: 1,
              my: 1,
              px: 1.5,
              py: 0.85,
              borderRadius: 1,
              border: (theme) => `1px solid ${alpha(theme.palette.warning.main, 0.18)}`,
              bgcolor: (theme) => alpha(theme.palette.warning.light, theme.palette.mode === "light" ? 0.18 : 0.08),
            }}
          >
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, minWidth: 0 }}>
              <Typography component="span" sx={{ ...cellSx, fontWeight: 700 }}>
                Total
              </Typography>
              {row.isTaxPresent && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: "text.secondary", fontSize: "0.72rem", fontWeight: 600 }}
                >
                  (incl. taxes)
                </Typography>
              )}
            </Box>
            <Typography
              sx={{ ...cellSx, textAlign: "center", fontWeight: 700 }}
            >
              {totalRow.unit} units
            </Typography>
            <Typography
              sx={{
                ...cellSx,
                textAlign: "center",
                fontWeight: 700,
                color: marginColor(totalRow.margin),
              }}
            >
              {formatPct(totalRow.margin)}
            </Typography>
            <Typography
              sx={{
                ...cellSx,
                textAlign: "right",
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatMoney(totalRow.amount)}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              px: 1.5,
              pb: 1.1,
              color: "text.secondary",
              fontSize: "0.74rem",
            }}
          >
            Total includes product variations and selected decoration charges.
          </Typography>
        </>
      )}
    </Box>
  );
};

const PricingCollapsedSummary = ({ row }) => {
  const totalRow = row.data?.find((r) => r.title === "Total");

  const explicitSubtotal = Number(totalRow?.taxBreakdown?.[0]?.taxableAmount);

  const subtotal = Number.isFinite(explicitSubtotal)
    ? explicitSubtotal
    : totalRow
      ? totalRow.amount - (totalRow.taxAmount || 0)
      : 0;

  const total = totalRow?.amount ?? 0;
  
  const marginPct = totalRow?.margin ?? 0;
  const units = totalRow?.unit ?? 0;

  const pricingCurrencyCode = String(row?.pricingCurrencyCode || "").toUpperCase();
  const totalLabel = pricingCurrencyCode
    ? `Total (incl. tax) (${pricingCurrencyCode})`
    : "Total (incl. tax)";

  return (
    <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 0.5 }}>
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontSize: "0.72rem",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          Units
        </Typography>
        <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
          {units}
        </Typography>
      </Box>
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontSize: "0.72rem",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          Margin
        </Typography>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "0.9rem",
            color: marginColor(marginPct),
          }}
        >
          {formatPct(marginPct)}
        </Typography>
      </Box>
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontSize: "0.72rem",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          Subtotal
        </Typography>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "0.9rem",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatMoney(subtotal)}
        </Typography>
      </Box>
      {row.isTaxPresent && (
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "0.72rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {totalLabel}
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.9rem",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatMoney(total)}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

export const ProductCard = ({
  view = "list",
  accessibleBreakdown = false,
  actionRestrictions = null,
  row,
  index,
  expanded,
  onToggleBreakdown,
  onEdit,
  onDelete,
  onCopy,
  onViewDetails = null,
  editable = true,
  capabilities = null,
  deleteProtectionEnabled = true,
  protectedOrderItemIds = new Set(),
  dragHandleProps = {},
  dragDisabled = false,
  isDragging = false,
}) => {
  const [hovered, setHovered] = useState(false);
  const orderItemId = Number(row?.orderItemId ?? row?.dbItemId);
  const isProtected =
    deleteProtectionEnabled &&
    Number.isInteger(orderItemId) &&
    protectedOrderItemIds.has(orderItemId);
  const canEdit = editable && capabilities?.editItem !== false && !actionRestrictions?.edit;
  const canCopy = editable && capabilities?.copyItem !== false && !actionRestrictions?.copy;
  const canDelete = editable && capabilities?.deleteItem !== false;
  const deleteDisabled = !canDelete || isProtected;
  const deleteTooltip = isProtected ? PROTECTED_ORDER_ITEM_DELETE_MESSAGE : "Delete item";
  const stopCardDoubleClick = (event) => event.stopPropagation();
  const runActionOnce = (event, action) => {
    event.stopPropagation();
    // A physical double-click emits two click events before dblclick. The first
    // click owns the explicit action; ignore the second so actions run once.
    if (event.detail > 1) return;
    action(row);
  };

  const actions = (
        <Box
          onDoubleClick={stopCardDoubleClick}
          sx={{
            display: "flex",
            flexDirection: view === "list" ? (onViewDetails ? { xs: "row", sm: "column" } : "column") : "row",
            ...(onViewDetails && view === "list" && { width: { xs: "100%", sm: "auto" }, flexWrap: "wrap" }),
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            px: 1,
            borderLeft: (theme) =>
              `1px solid ${alpha(theme.palette.divider, 0.7)}`,
            bgcolor: (theme) =>
              hovered
                ? alpha(
                    theme.palette.grey[50],
                    theme.palette.mode === "light" ? 1 : 0.04,
                  )
                : "transparent",
            transition: "background 0.18s ease",
            minWidth: onViewDetails ? 100 : 48,
          }}
        >
          {onViewDetails && <Button size="small" disabled={!editable}
            onClick={(event) => runActionOnce(event, onViewDetails)}>View details</Button>}
          {view === "list" && !actionRestrictions?.reorder && <Tooltip title="Drag to reorder" arrow>
            <span>
              <IconButton
                size="small"
                aria-label="Reorder product"
                title="Drag to reorder"
                disabled={dragDisabled}
                onClick={stopCardDoubleClick}
                onDoubleClick={stopCardDoubleClick}
                sx={{
                  color: "text.secondary",
                  cursor: isDragging ? "grabbing" : "grab",
                  "&:hover": {
                    color: "text.primary",
                    bgcolor: (theme) => alpha(theme.palette.grey[500], 0.1),
                  },
                  "&.Mui-disabled": {
                    cursor: "default",
                  },
                }}
                {...dragHandleProps}
              >
                <DragIndicatorIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>}

          <Tooltip title={actionRestrictions?.edit || "Edit item"} describeChild arrow>
            <span tabIndex={actionRestrictions?.edit ? 0 : undefined} aria-label={actionRestrictions?.edit || undefined}>
            <IconButton
              size="small"
              aria-label="Edit item"
              disabled={!canEdit}
              onClick={(event) => runActionOnce(event, onEdit)}
              onDoubleClick={stopCardDoubleClick}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "primary.main",
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={actionRestrictions?.copy || "Copy item"} describeChild arrow>
            <span tabIndex={actionRestrictions?.copy ? 0 : undefined} aria-label={actionRestrictions?.copy || undefined}>
            <IconButton
              size="small"
              aria-label="Copy item"
              disabled={!canCopy}
              onClick={(event) => runActionOnce(event, onCopy)}
              onDoubleClick={stopCardDoubleClick}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "text.primary",
                  bgcolor: (theme) => alpha(theme.palette.grey[500], 0.1),
                },
              }}
            >
              <ContentCopyOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={deleteTooltip} arrow>
            <span>
              <IconButton
                size="small"
                aria-label="Delete item"
                disabled={deleteDisabled}
                onClick={(event) => runActionOnce(event, onDelete)}
                onDoubleClick={stopCardDoubleClick}
                sx={{
                  color: "text.secondary",
                  opacity: isProtected ? 0.5 : 1,
                  "&:hover": {
                    color: "error.main",
                    bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
                  },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
  );
  if (view !== "list") return <ShowcaseProductCard row={row} view={view} actions={actions}
    summary={<PricingCollapsedSummary row={row} />} breakdown={<PricingBreakdownTable row={row} />} />;

  return (
    <Paper
      elevation={0}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDoubleClick={() => {
        if (canEdit) onEdit(row);
      }}
      sx={{
        border: (theme) =>
          `1px solid ${
            hovered
              ? alpha(theme.palette.primary.main, 0.25)
              : alpha(theme.palette.divider, 0.9)
          }`,
        borderRadius: 1,
        overflow: "hidden",
        transition:
          "border-color 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease",
        boxShadow: isDragging
          ? (theme) => `0 12px 28px ${alpha(theme.palette.common.black, 0.18)}`
          : hovered
          ? (theme) => `0 4px 16px ${alpha(theme.palette.common.black, 0.07)}`
          : "none",
        opacity: isDragging ? 0.94 : 1,
        cursor: canEdit ? "pointer" : "default",
      }}
    >
      <Box sx={{ display: "flex", gap: 0, alignItems: "stretch", ...(onViewDetails && { flexWrap: { xs: "wrap", sm: "nowrap" } }) }}>
        <Box
          sx={{
            width: onViewDetails ? { xs: 80, sm: 140 } : 140,
            minWidth: onViewDetails ? { xs: 80, sm: 140 } : 140,
            bgcolor: (theme) =>
              alpha(
                theme.palette.grey[100],
                theme.palette.mode === "light" ? 1 : 0.06,
              ),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRight: (theme) =>
              `1px solid ${alpha(theme.palette.divider, 0.7)}`,
            flexShrink: 0,
            overflow: "hidden",
            position: "relative",
          }}
        >
          {row.image ? (
            <Box
              component="img"
              src={row.image}
              alt={row.name || "Product image"}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
              sx={{
                width: "100%",
                height: 140,
                objectFit: "contain",
                display: "block",
              }}
            />
          ) : null}
          <Box
            sx={{
              display: row.image ? "none" : "flex",
              width: "100%",
              height: 140,
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 0.5,
              color: "text.disabled",
            }}
          >
            <ImageNotSupportedOutlinedIcon sx={{ fontSize: 32 }} />
            <Typography variant="caption" sx={{ fontSize: "0.68rem" }}>
              No image
            </Typography>
          </Box>
        </Box>

        <Box sx={{ flex: 1, p: { xs: 1.25, md: 1.5 }, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              fontSize: "0.95rem",
              color: "text.primary",
              mb: 0.25,
              lineHeight: 1.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: onViewDetails ? "normal" : "nowrap",
              ...(onViewDetails && { overflowWrap: "anywhere" }),
            }}
          >
            {row.name || "—"}
          </Typography>

          <Stack
            direction="row"
            spacing={1.5}
            flexWrap="wrap"
            sx={{ mb: 0.75 }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <TagIcon sx={{ fontSize: 14, color: "text.disabled" }} />
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.78rem",
                  fontFamily: "monospace",
                }}
              >
                {row.productIdDisplay}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <BusinessOutlinedIcon
                sx={{ fontSize: 14, color: "text.disabled" }}
              />
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontSize: "0.78rem" }}
              >
                {row.supplier}
              </Typography>
            </Stack>
            {Array.isArray(row.categories) && row.categories.length > 0 && (
              <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                {row.categories.map((category) => (
                  <Chip
                    key={category.id}
                    size="small"
                    label={category.name}
                    sx={{
                      height: 22,
                      fontSize: "0.74rem",
                      fontWeight: 700,
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                      color: "primary.main",
                      "& .MuiChip-label": { px: 0.9 },
                    }}
                  />
                ))}
              </Stack>
            )}
          </Stack>

          {row.shortDescription && (
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.82rem",
                color: "text.secondary",
                mb: 0.5,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {row.shortDescription}
            </Typography>
          )}

          {!expanded && <PricingCollapsedSummary row={row} />}
        </Box>

        {actions}
      </Box>

      <Box
        sx={{
          borderTop: (theme) =>
            `1px solid ${alpha(theme.palette.divider, 0.7)}`,
          px: 1.5,
          py: 0.75,
          bgcolor: (theme) =>
            alpha(
              theme.palette.grey[50],
              theme.palette.mode === "light" ? 0.6 : 0.03,
            ),
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          userSelect: "none",
          "&:hover": {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
          },
          transition: "background 0.15s ease",
        }}
        component={accessibleBreakdown ? "button" : "div"}
        type={accessibleBreakdown ? "button" : undefined}
        aria-expanded={accessibleBreakdown ? expanded : undefined}
        style={accessibleBreakdown ? { width: "100%", borderLeft: 0, borderRight: 0, borderBottom: 0, textAlign: "left" } : undefined}
        onClick={() => onToggleBreakdown(row.itemId)}
        onDoubleClick={stopCardDoubleClick}
      >
        <Typography
          component={accessibleBreakdown ? "span" : "p"}
          variant="body2"
          sx={{
            color: "#c3171d",
            fontWeight: 600,
            fontSize: "0.82rem",
            flex: 1,
          }}
        >
          {expanded ? "Hide pricing breakdown" : "Show pricing breakdown"}
        </Typography>
        {expanded ? (
          <ExpandLessIcon sx={{ fontSize: 18, color: "#c3171d" }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 18, color: "#c3171d" }} />
        )}
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <PricingBreakdownTable row={row} />
        </Box>
      </Collapse>
    </Paper>
  );
};

const SortableProductCard = ({ row, dragDisabled = false, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: row?.layoutId || row?.itemId,
    disabled: dragDisabled,
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <ProductCard
        {...props}
        row={row}
        dragDisabled={dragDisabled}
        isDragging={isDragging}
        dragHandleProps={
          dragDisabled
            ? {}
            : {
                ...attributes,
                ...listeners,
              }
        }
      />
    </Box>
  );
};

const SectionRow = ({
  row,
  onEdit,
  onDelete,
  editable = true,
  dragHandleProps = {},
  dragDisabled = false,
  isDragging = false,
}) => {
  const stop = (event) => event.stopPropagation();

  return (
    <Paper
      elevation={0}
      sx={{
        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        borderRadius: 1,
        px: 1.5,
        py: 1.25,
        display: "flex",
        alignItems: "center",
        gap: 1,
        bgcolor: (theme) => alpha(theme.palette.grey[100], 0.75),
        boxShadow: isDragging
          ? (theme) => `0 12px 28px ${alpha(theme.palette.common.black, 0.18)}`
          : "none",
        opacity: isDragging ? 0.94 : 1,
      }}
    >
      <Tooltip title="Drag to reorder" arrow>
        <span>
          <IconButton
            size="small"
            aria-label="Reorder section"
            title="Drag to reorder"
            disabled={dragDisabled}
            onClick={stop}
            onDoubleClick={stop}
            sx={{
              color: "text.secondary",
              cursor: isDragging ? "grabbing" : "grab",
              "&.Mui-disabled": { cursor: "default" },
            }}
            {...dragHandleProps}
          >
            <DragIndicatorIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>

      <HorizontalRuleIcon sx={{ fontSize: 16, color: "text.disabled" }} />
      <Typography
        variant="subtitle2"
        sx={{
          flex: 1,
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
        }}
      >
        {row?.title || "Untitled Section"}
      </Typography>

      <Chip
        size="small"
        label={normalizeSectionStyle(row?.style) === "dark" ? "Dark" : "Light"}
        variant={normalizeSectionStyle(row?.style) === "dark" ? "filled" : "outlined"}
        sx={{
          height: 22,
          fontWeight: 700,
          bgcolor: normalizeSectionStyle(row?.style) === "dark" ? "#111" : "transparent",
          color: normalizeSectionStyle(row?.style) === "dark" ? "#fff" : "text.secondary",
          borderColor: "divider",
        }}
      />

      <Tooltip title="Edit section title" arrow>
        <IconButton
          size="small"
          aria-label="Edit section"
          disabled={!editable}
          onClick={(event) => {
            stop(event);
            onEdit(row);
          }}
        >
          <EditOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Tooltip title="Delete section title" arrow>
        <IconButton
          size="small"
          aria-label="Delete section"
          disabled={!editable}
          onClick={(event) => {
            stop(event);
            onDelete(row);
          }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Paper>
  );
};

const SortableSectionRow = ({ row, dragDisabled = false, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: row?.layoutId,
    disabled: dragDisabled,
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <SectionRow
        {...props}
        row={row}
        dragDisabled={dragDisabled}
        isDragging={isDragging}
        dragHandleProps={
          dragDisabled
            ? {}
            : {
                ...attributes,
                ...listeners,
              }
        }
      />
    </Box>
  );
};

const OtherServiceCard = ({ row, onEdit, onDelete, editable = true }) => {
  const [hovered, setHovered] = useState(false);

  const marginPct = (() => {
    const q = Number(row?.Quantity) || 0;
    const unitCostC = toCents(Number(row?.UnitCost) || 0);
    const taxRatePct = Number(row?.Taxes) || 0;
    const totalC = toCents(Number(row?.Total) || 0);
    const subtotalC = taxRatePct
      ? Math.round(totalC / (1 + taxRatePct / 100))
      : totalC;
    const retailC = toCents(Number(row?.RetailPrice) || 0);
    const revenueC = retailC > 0 && q > 0 ? retailC * q : subtotalC;
    const profitC = revenueC - unitCostC * q;
    return revenueC > 0 ? (profitC / revenueC) * 100 : Number(row?.Margin) || 0;
  })();

  const { subtotal, total, hasTax } = (() => {
    const totalVal = Number(row?.Total) || 0;
    const taxRatePct = Number(row?.Taxes) || 0;
    const totalC = toCents(totalVal);
    const subtotalC = taxRatePct
      ? Math.round(totalC / (1 + taxRatePct / 100))
      : totalC;
    return {
      subtotal: fromCents(subtotalC),
      total: fromCents(totalC),
      hasTax: taxRatePct > 0,
    };
  })();

  return (
    <Paper
      elevation={0}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        border: (theme) =>
          `1px solid ${
            hovered
              ? alpha(theme.palette.primary.main, 0.25)
              : alpha(theme.palette.divider, 0.9)
          }`,
        borderRadius: 1,
        overflow: "hidden",
        transition: "border-color 0.18s ease, box-shadow 0.18s ease",
        boxShadow: hovered
          ? (theme) => `0 4px 16px ${alpha(theme.palette.common.black, 0.07)}`
          : "none",
      }}
    >
      <Box sx={{ display: "flex", gap: 0, alignItems: "stretch" }}>
        <Box
          sx={{
            width: 72,
            minWidth: 72,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: (theme) =>
              alpha(
                theme.palette.grey[100],
                theme.palette.mode === "light" ? 1 : 0.06,
              ),
            borderRight: (theme) =>
              `1px solid ${alpha(theme.palette.divider, 0.7)}`,
            flexShrink: 0,
          }}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              color: "primary.main",
            }}
          >
            <MiscellaneousServicesOutlinedIcon sx={{ fontSize: 22 }} />
          </Avatar>
        </Box>

        <Box sx={{ flex: 1, p: { xs: 1.25, md: 1.5 }, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 0.75 }}
            flexWrap="wrap"
          >
            {renderTypeBadge(row?.Type)}
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                fontSize: "0.92rem",
                color: "text.primary",
                lineHeight: 1.3,
              }}
            >
              {row?.Title || "No Title"}
            </Typography>
          </Stack>

          {row?.Description && (
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.82rem",
                color: "text.secondary",
                mb: 0.75,
                lineHeight: 1.4,
              }}
            >
              {row.Description}
            </Typography>
          )}

          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Units
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: "0.88rem" }}>
                {unitsLabel(row?.Quantity)}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Margin
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.88rem",
                  color: marginColor(marginPct),
                }}
              >
                {formatPct(marginPct)}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                {hasTax ? "Subtotal" : "Total"}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.88rem",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {formatMoney(subtotal)}
              </Typography>
            </Box>
            {hasTax && (
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Total (incl. tax)
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.88rem",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatMoney(total)}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            px: 1,
            borderLeft: (theme) =>
              `1px solid ${alpha(theme.palette.divider, 0.7)}`,
            minWidth: 48,
          }}
        >
          <Tooltip title="Edit service" arrow>
            <IconButton
              size="small"
              disabled={!editable}
              onClick={() => onEdit(row)}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "#0D5EA6",
                  bgcolor: "rgba(13,94,166,0.08)",
                },
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete service" arrow>
            <IconButton
              size="small"
              disabled={!editable}
              onClick={() => onDelete(row.id ?? row.cartServiceId)}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "error.main",
                  bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
                },
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
};

const CartSummaryBar = ({ orderSummary, onDebugOpen, summaryLabel = "Cart Summary" }) => {
  const tiles = [
    {
      label: "Booked Margin $",
      value: formatMoney(orderSummary.bookedMarginAmount),
      icon: <AttachMoneyIcon sx={{ fontSize: 20 }} />,
      color: marginColor(orderSummary.bookedMarginPct),
      tooltip: null,
    },
    {
      label: "Booked Margin %",
      value: formatPct(orderSummary.bookedMarginPct),
      icon: <TrendingUpIcon sx={{ fontSize: 20 }} />,
      color: marginColor(orderSummary.bookedMarginPct),
      tooltip: `Booked Margin % = (Margin Amount ÷ Subtotal) × 100 = (${orderSummary.bookedMarginAmount.toFixed(
        2,
      )} ÷ ${orderSummary.subtotal.toFixed(
        2,
      )}) × 100 = ${orderSummary.bookedMarginPct.toFixed(
        2,
      )}%. Retail is stored in cents, so values can be slightly above/below the typed target margin.`,
    },
    {
      label: "Subtotal",
      value: formatMoney(orderSummary.subtotal),
      icon: <ReceiptOutlinedIcon sx={{ fontSize: 20 }} />,
      color: "text.primary",
      tooltip: null,
    },
    {
      label: "Total (incl. tax)",
      value: formatMoney(orderSummary.totalInclTax),
      icon: <AttachMoneyIcon sx={{ fontSize: 20 }} />,
      color: "text.primary",
      tooltip: null,
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        overflow: "hidden",
        mt: 0,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: (theme) =>
            `1px solid ${alpha(theme.palette.divider, 0.7)}`,
          bgcolor: (theme) =>
            alpha(
              theme.palette.grey[50],
              theme.palette.mode === "light" ? 1 : 0.04,
            ),
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <ShoppingCartOutlinedIcon
            sx={{ fontSize: 18, color: "text.secondary" }}
          />
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: "text.primary", fontSize: "0.9rem" }}
          >
            {summaryLabel}
          </Typography>
        </Stack>
        <Tooltip title="View raw cart-summary debug payload" arrow>
          <IconButton
            size="small"
            onClick={onDebugOpen}
            aria-label="Open cart summary debug payload"
            sx={{
              color: "text.disabled",
              "&:hover": { color: "text.secondary" },
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          bgcolor: "background.paper",
        }}
      >
        {tiles.map((tile, i) => (
          <React.Fragment key={tile.label}>
            {i > 0 && (
              <Divider
                orientation="vertical"
                flexItem
                sx={{ display: { xs: "none", md: "block" } }}
              />
            )}
            <Box sx={{ p: { xs: 1.5, md: 2 } }}>
              {tile.tooltip ? (
                <Tooltip arrow placement="top" title={tile.tooltip}>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{ mb: 0.5, cursor: "help", width: "fit-content" }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      {tile.label}
                    </Typography>
                    <InfoOutlinedIcon
                      sx={{ fontSize: 13, color: "text.disabled" }}
                    />
                  </Stack>
                </Tooltip>
              ) : (
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    display: "block",
                    mb: 0.5,
                  }}
                >
                  {tile.label}
                </Typography>
              )}
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  color: tile.color,
                  fontVariantNumeric: "tabular-nums",
                  lineHeight: 1.2,
                }}
              >
                {tile.value}
              </Typography>
            </Box>
          </React.Fragment>
        ))}
      </Box>
    </Paper>
  );
};

const EmptyCartState = ({ onAddProduct, onAddService, canAddProduct = true, canAddService = true, showcase = false }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      py: { xs: 6, md: 8 },
      px: 3,
      textAlign: "center",
    }}
  >
    <Avatar
      sx={{
        width: 64,
        height: 64,
        bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
        color: "error.main",
        mb: 2,
      }}
    >
      <AddShoppingCartIcon sx={{ fontSize: 32 }} />
    </Avatar>
    <Typography
      variant="h6"
      sx={{ fontWeight: 700, mb: 0.75, color: "text.primary" }}
    >
      {showcase ? 'No products on this page' : 'Your cart is empty'}
    </Typography>
    <Typography
      variant="body2"
      sx={{ color: "text.secondary", mb: 3, maxWidth: "36ch" }}
    >
      {showcase ? 'Clear your search or add a product. Website membership is unchanged by browsing.'
        : 'Add products from the database or create custom services to start building this order.'}
    </Typography>
    {!showcase && <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
      <Button
        variant="contained"
        color="error"
        startIcon={<AddIcon />}
        onClick={onAddProduct}
        disabled={!canAddProduct}
        sx={{
          textTransform: "none",
          borderRadius: 2,
          fontWeight: 700,
          boxShadow: "none",
          px: 2.5,
        }}
      >
        Add Product
      </Button>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<AddIcon />}
        onClick={onAddService}
        disabled={!canAddService}
        sx={{
          textTransform: "none",
          borderRadius: 2,
          fontWeight: 700,
          px: 2.5,
        }}
      >
        Add Other Service
      </Button>
    </Stack>}
  </Box>
);

const SelectedProducts = ({
  order,
  fetchOrder,
  handleShowProductsPopup,
  isPromoStore = false,
  onOpenCartHistory = null,
  cartWorkspace = null,
  salesOrderCartUnlocked = false,
  onUnlockSalesOrderCart = null,
  onSavePromoStoreProduct = null,
  onWorkspaceMutationCommitted = null,
  onReorderProducts = null,
  onReorderLayout = null,
  onAddAdjustment = null,
  secondaryActionLabel = "Add Title",
  onSecondaryAction = null,
  promoStoreCategories = [],
  canSeeManualPricingToggle = false,
  showcaseProductViewScope = null,
  showcaseActions = null,
  renderShowcaseToolbar = null,
  showcaseFooter = null,
}) => {
  const [productView, selectProductView] = useShowcaseProductView(showcaseProductViewScope);
  const enableToastContainer = !String(React?.version || "").startsWith("19");
  const { orderId } = useParams();

  const isWorkspaceMode = !!cartWorkspace;
  const editable = showcaseActions ? !showcaseActions.busy : cartWorkspace?.editable ?? true;
  const capabilities = cartWorkspace?.capabilities || {};
  const canAddProduct = editable && capabilities.addProduct !== false;
  const canAddService = !showcaseActions && editable && capabilities.addOtherService !== false;
  const canEditSections = !showcaseActions && editable && capabilities.sections !== false;
  const source = cartWorkspace?.source ?? "order-cart";
  const isWorkspaceMutationMode = isWorkspaceMode && source !== "order-cart";
  const isPromoStoreMutationMode =
    isPromoStore && typeof onSavePromoStoreProduct === "function";
  const shouldUseControlledEdit =
    isWorkspaceMutationMode || isPromoStoreMutationMode || !!showcaseActions;
  const deleteProtectionEnabled = source === "order-cart";

  const [openPopup, setOpenPopup] = useState(false);
  const [showcaseDetailsProduct, setShowcaseDetailsProduct] = useState(null);
  const viewShowcaseDetails = (row) => {
    const key = row.showcaseIdentity?.key;
    if (key) setShowcaseDetailsProduct((order?.Products || []).find(product => product.showcaseIdentity?.key === key) || null);
  };
  useEffect(() => {
    const onChange = showcaseActions?.onEditorStateChange;
    onChange?.(openPopup || !!showcaseDetailsProduct);
    return () => onChange?.(false);
  }, [openPopup, showcaseDetailsProduct, showcaseActions?.onEditorStateChange]);
  const [otherServicePopup, setOtherServicePopup] = useState(false);
  const [otherServiceId, setOtherServiceId] = useState(null);
  const [orderItemId, setOrderItemId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedOtherService, setSelectedOtherService] = useState(null);
  const [openCartMathDebug, setOpenCartMathDebug] = useState(false);
  const [fetchedOtherServiceData, setFetchedOtherServiceData] = useState(null);
  const [expandedBreakdowns, setExpandedBreakdowns] = useState({});
  const [savingWorkspaceCart, setSavingWorkspaceCart] = useState(false);
  const [refreshingOrderCart, setRefreshingOrderCart] = useState(false);
  const [savingOrderCartItem, setSavingOrderCartItem] = useState(false);
  const refreshInFlightRef = useRef(null);
  const trackedFetchOrder = useCallback((options) => {
    if (typeof fetchOrder !== "function") return Promise.resolve();
    // A post-save refresh must start AFTER persistence, never reuse an older
    // request that may have read the previous cart quantity.
    setRefreshingOrderCart(true);
    const pending = Promise.resolve().then(() => fetchOrder(options));
    refreshInFlightRef.current = pending;
    return pending.finally(() => {
      if (refreshInFlightRef.current === pending) {
        refreshInFlightRef.current = null;
        setRefreshingOrderCart(false);
      }
    });
  }, [fetchOrder]);
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [sectionDialogMode, setSectionDialogMode] = useState("add");
  const [sectionTitleDraft, setSectionTitleDraft] = useState("");
  const [sectionStyleDraft, setSectionStyleDraft] = useState("light");
  const [selectedSection, setSelectedSection] = useState(null);

  const getProductWorkspaceIdentity = useCallback(
    (row = {}) => buildProductWorkspaceIdentity(row),
    [],
  );

  const handleSaveWorkspaceCart = useCallback(async () => {
    if (!cartWorkspace?.save) return;

    try {
      setSavingWorkspaceCart(true);
      const result = await cartWorkspace.save();

      if (
        result?.ok &&
        source === "invoicing-cart" &&
        typeof cartWorkspace.reload === "function"
      ) {
        await cartWorkspace.reload();
      }

      toast.success("Saved Invoicing Cart — Sales Order remains unchanged.");
    } catch (err) {
      console.error("Failed to save invoicing cart", err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save Invoicing Cart.",
      );
    } finally {
      setSavingWorkspaceCart(false);
    }
  }, [cartWorkspace, source]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    if (source !== "invoicing-cart") return;

    console.debug("[SelectedPresentation][InvoicingCartSaveState]", {
      source,
      editable,
      dirty: cartWorkspace?.dirty,
      loading: cartWorkspace?.loading,
      selectedInvoicingCartKey: cartWorkspace?.selectedInvoicingCartKey,
      lifecycle: cartWorkspace?.selectedInvoicingCartLifecycle,
      editReason: cartWorkspace?.editReason,
    });
  }, [
    source,
    editable,
    cartWorkspace?.dirty,
    cartWorkspace?.loading,
    cartWorkspace?.selectedInvoicingCartKey,
    cartWorkspace?.selectedInvoicingCartLifecycle,
    cartWorkspace?.editReason,
  ]);

  const handleEditClick = useCallback(
    (row) => {
      if (!editable) return;
      if (showcaseActions && row.showcaseIdentity?.kind !== 'direct') {
        return;
      }

      // Order Cart is live OrderItem state.
      // Sales Order Cart and Invoicing Cart are snapshot/workspace state.
      // Do not use one cart's persistence semantics for another.
      if (source === "order-cart" && !shouldUseControlledEdit) {
        const liveItems = Array.isArray(order?.Items) && order.Items.length
          ? order.Items
          : order?.Products || [];
        const rowOrderItemId =
          row?.orderItemId ??
          row?.dbItemId ??
          row?.persistedOrderItemId ??
          null;
        const item = liveItems.find(
          (p) =>
            (p?.cartLineId != null &&
              row?.itemId != null &&
              String(p.cartLineId) === String(row.itemId)) ||
            (p?.id != null &&
              row?.itemId != null &&
              String(p.id) === String(row.itemId)) ||
            (p?.id != null &&
              rowOrderItemId != null &&
              String(p.id) === String(rowOrderItemId)),
        );

        if (!item) {
          alert("❌ No matching item — check your console.");
          return;
        }

        const liveOrderItemId = item?.id ?? null;
        setOrderItemId(liveOrderItemId);
        setSelectedItem({
          ...item,
          OrderItemId: liveOrderItemId,
          orderItemId: liveOrderItemId,
          dbItemId: liveOrderItemId,
          persistedOrderItemId: liveOrderItemId,
          sourceOrderItemId:
            item?.sourceOrderItemId ??
            item?.SourceOrderItemId ??
            item?.originalOrderItemId ??
            item?.OriginalOrderItemId ??
            liveOrderItemId,
        });
        setOpenPopup(true);
        return;
      }

      const sourceItems = order?.Products || [];
      const cartLineMatches = sourceItems.filter(
        (p) =>
          p?.cartLineId != null &&
          row?.itemId != null &&
          String(p.cartLineId) === String(row.itemId),
      );
      const item = cartLineMatches.length === 1
        ? cartLineMatches[0]
        : sourceItems.find(
            (p) =>
              p?.id != null &&
              row?.itemId != null &&
              String(p.id) === String(row.itemId),
          );

      if (!item) {
        alert("❌ No matching item — check your console.");
        return;
      }

      const identity = buildProductWorkspaceIdentity({
        ...item,
        itemId: row?.itemId,
        cartLineId: item?.cartLineId ?? row?.cartLineId,
        dbItemId: row?.dbItemId,
        orderItemId: row?.orderItemId,
        persistedOrderItemId: row?.persistedOrderItemId,
        sourceOrderItemId: row?.sourceOrderItemId,
      });
      const persistedOrderItemId = identity.OrderItemId ?? null;
      const sourceOrderItemId = identity.sourceOrderItemId ?? null;

      setOrderItemId(persistedOrderItemId);
      setSelectedItem({
        ...item,
        cartLineId: identity.cartLineId ?? item?.cartLineId,
        OrderItemId: persistedOrderItemId,
        orderItemId: persistedOrderItemId,
        dbItemId: persistedOrderItemId,
        persistedOrderItemId,
        sourceOrderItemId,
        originalOrderItemId: item?.originalOrderItemId ?? sourceOrderItemId,
      });
      setOpenPopup(true);
    },
    [editable, order, shouldUseControlledEdit, source, showcaseActions],
  );

  const showcaseRestrictions = (row) => showcaseActions ? {
    edit: row.showcaseIdentity?.kind !== 'direct' ? 'Catalogue products cannot be edited here. Use Catalogues to hide or show them, or add a custom product.' : null,
    copy: 'Duplicate is unavailable for Showcase. Add a custom product instead.',
    reorder: true,
  } : null;

  const protectedOrderItemIds = useMemo(
    () => new Set((order?.protectedOrderItemIds || []).map((id) => Number(id)).filter(Number.isFinite)),
    [order?.protectedOrderItemIds],
  );

  const handleDeleteClick = useCallback(
    async (row) => {
      if (!editable) return;
      if (
        shouldBlockProtectedOrderItemDeletion({
          source,
          row,
          protectedOrderItemIds,
        })
      ) {
        toast.error(PROTECTED_ORDER_ITEM_DELETE_MESSAGE);
        return;
      }
      if (!window.confirm("Are you sure you want to delete this item?")) return;

      try {
        if (showcaseActions) {
          await showcaseActions.mutate('remove', row.showcaseIdentity?.key);
          return;
        }
        if (
          isWorkspaceMutationMode &&
          cartWorkspace?.mutationHandlers?.deleteProduct
        ) {
          const identity = getProductWorkspaceIdentity(row);
          const mutationResult = await commitWorkspaceMutation({
            source,
            mutationType: "delete-product",
            mutate: () => cartWorkspace.mutationHandlers.deleteProduct(identity),
            metadata: { identity },
            onWorkspaceMutationCommitted,
          });
          if (!mutationResult?.ok) {
            toast.error("Unable to match the item to delete. Refresh and try again.");
          }
          return;
        }

        if (isPromoStoreMutationMode) {
          const products = Array.isArray(order?.Products)
            ? order.Products
            : [];
          const identity = getProductWorkspaceIdentity(row);

          const nextProducts = products.filter((product) => {
            if (!product) return true;

            const promoStoreItemId =
              product.id ??
              product.PromoStoreItemId ??
              product.promoStoreItemId ??
              product.itemId ??
              null;

            const sameCartLine =
              identity.cartLineId != null &&
              product.cartLineId != null &&
              String(product.cartLineId) === String(identity.cartLineId);

            const samePersistedId =
              identity.id != null &&
              promoStoreItemId != null &&
              String(promoStoreItemId) === String(identity.id);

            return !(sameCartLine || samePersistedId);
          });

          const removedProducts = products.filter((product) => !nextProducts.includes(product));
          if (removedProducts.length !== 1) {
            console.error("Ambiguous or missing PromoStore product identity", row);
            toast.error("Unable to identify a single PromoStore item to delete. Refresh and try again.");
            return;
          }

          // Never match by catalogue ProductId: different saved PromoStore
          // products can share the same supplier product.
          const removed = removedProducts[0];
          const itemId =
            removed?.id ??
            removed?.PromoStoreItemId ??
            removed?.promoStoreItemId ??
            removed?.itemId;
          const storeId = order?.id ?? order?.PromoStoreId;
          if (!Number.isSafeInteger(Number(itemId)) || Number(itemId) <= 0 ||
              !Number.isSafeInteger(Number(storeId)) || Number(storeId) <= 0) {
            toast.error("Unable to identify this PromoStore product. Refresh and try again.");
            return;
          }
          await deletePromoStoreItem(storeId, itemId);
          await fetchOrder?.();
          return;
        }

        const orderItemId = resolvePersistedOrderItemDeleteId(row);

        if (!orderItemId) {
          console.error("Missing OrderItem id for delete", row);
          toast.error("Missing item id — could not delete item");
          return;
        }

        await deleteOrderItem(orderItemId);
        // Rehydrate the cart after deletion without resetting the page UI.
        await fetchOrder?.({ suppressLoader: true });
      } catch (err) {
        console.error("Delete failed", err);
        const message = err?.response?.data?.message || "Failed to delete item";
        toast.error(message);
        if (err?.response?.status === 409) {
          await fetchOrder?.({ suppressLoader: true });
        }
      }
    },
    [
      editable,
      isWorkspaceMutationMode,
      cartWorkspace,
      getProductWorkspaceIdentity,
      fetchOrder,
      isPromoStoreMutationMode,
      showcaseActions,
      onSavePromoStoreProduct,
      onWorkspaceMutationCommitted,
      order?.Products,
      source,
      protectedOrderItemIds,
      source,
    ],
  );

  const handleCopyClick = useCallback(
    async (row) => {
      if (!editable) return;
      if (showcaseActions) {
        return;
      }

      try {
        if (
          isWorkspaceMutationMode &&
          cartWorkspace?.mutationHandlers?.copyProduct
        ) {
          const identity = getProductWorkspaceIdentity(row);
          const mutationResult = await commitWorkspaceMutation({
            source,
            mutationType: "copy-product",
            mutate: () => cartWorkspace.mutationHandlers.copyProduct(identity),
            metadata: { identity },
            onWorkspaceMutationCommitted,
          });
          if (!mutationResult?.ok) {
            toast.error("Unable to match the item to copy. Refresh and try again.");
          }
          return;
        }

        if (isPromoStoreMutationMode) {
          const products = Array.isArray(order?.Products) ? order.Products : [];
          const identity = getProductWorkspaceIdentity(row);
          const sourceProduct = products.find((product) => {
            const productItemId =
              product?.id ??
              product?.PromoStoreItemId ??
              product?.promoStoreItemId ??
              product?.itemId ??
              null;
            return identity.id != null && productItemId != null &&
              String(productItemId) === String(identity.id);
          });

          if (!sourceProduct) {
            console.error("Missing PromoStore item for copy", row);
            toast.error("Missing PromoStore item - could not copy item");
            return;
          }

          await onSavePromoStoreProduct([
            ...products,
            clonePromoStoreProduct(sourceProduct),
          ]);
          toast.success("Item copied");
          return;
        }

        const orderItemId = resolvePersistedOrderItemCopyId(row);

        if (!orderItemId) {
          console.error("Missing OrderItem id for copy", row);
          toast.error("Missing item id - could not copy item");
          return;
        }

        await copyOrderItem(orderItemId);
        // The project Order Cart uses live OrderItems. Rehydrate its persisted
        // state without triggering the whole Project page loading screen.
        await fetchOrder?.({ suppressLoader: true });
        toast.success("Item copied");
      } catch (err) {
        console.error("Copy failed", err);
        toast.error("Failed to copy item");
      }
    },
    [
      editable,
      isWorkspaceMutationMode,
      cartWorkspace,
      getProductWorkspaceIdentity,
      fetchOrder,
      isPromoStoreMutationMode,
      showcaseActions,
      onSavePromoStoreProduct,
      onWorkspaceMutationCommitted,
      order?.Products,
    ],
  );

  const decorationCopyProducts = useMemo(() => {
    if (source === "order-cart" && !shouldUseControlledEdit) {
      if (Array.isArray(order?.Items) && order.Items.length) return order.Items;
      if (Array.isArray(order?.Products)) return order.Products;
      return [];
    }

    if (Array.isArray(order?.Products) && order.Products.length) return order.Products;
    if (Array.isArray(order?.Items)) return order.Items;
    return [];
  }, [order, shouldUseControlledEdit, source]);

  const handleCopyDecorationToProduct = useCallback(async ({
    targetProductKey,
    decoration,
    mode = "add",
    targetDecorationIndex = null,
  } = {}) => {
    const targetIndex = decorationCopyProducts.findIndex(
      (product, index) => getCartProductCopyKey(product, index) === targetProductKey
    );
    const targetProduct = targetIndex >= 0 ? decorationCopyProducts[targetIndex] : null;

    if (!targetProduct || !decoration) {
      toast.error("Unable to find the target product for this decoration copy.");
      return { ok: false, reason: "target-not-found" };
    }

    try {
      if (showcaseActions && targetProduct.showcaseIdentity?.kind !== 'direct') {
        toast.error('Catalogue decorations cannot be edited here. Choose a saved product instead.');
        return { ok: false, reason: 'catalogue-edit-unavailable' };
      }
      const verifiedDecoration = await resolveMatrixChargeTypesForCopy(
        decoration,
        getSingleDecoratorDetails
      );
      const updatedProduct = applyDecorationCopyToProduct(targetProduct, {
        decoration: verifiedDecoration,
        mode,
        targetDecorationIndex,
      });
      if (showcaseActions) {
        await showcaseActions.mutate('edit', targetProduct.showcaseIdentity.key, showcaseEditedProduct(targetProduct, updatedProduct));
        return { ok: true, product: updatedProduct };
      }
      if (
        isWorkspaceMutationMode &&
        cartWorkspace?.mutationHandlers?.editProduct
      ) {
        const mutationResult = await commitWorkspaceMutation({
          source,
          mutationType: "copy-decoration-to-product",
          mutate: () => cartWorkspace.mutationHandlers.editProduct(
            targetProduct,
            updatedProduct,
          ),
          metadata: {
            targetProductKey,
            mode,
            targetDecorationIndex,
            decorationLocation: decoration?.Location ?? decoration?.location ?? "",
          },
          onWorkspaceMutationCommitted,
        });

        if (!mutationResult?.ok) {
          toast.error("Unable to copy the decoration to that cart item. Refresh and try again.");
          return mutationResult || { ok: false };
        }

        toast.success("Decoration copied to product");
        return { ok: true, product: updatedProduct };
      }

      if (isPromoStoreMutationMode) {
        const products = Array.isArray(order?.Products) ? order.Products : [];
        const nextProducts = products.map((product, index) =>
          getCartProductCopyKey(product, index) === targetProductKey
            ? updatedProduct
            : product
        );
        await onSavePromoStoreProduct(nextProducts);
        toast.success("Decoration copied to product");
        return { ok: true, product: updatedProduct };
      }

      const targetOrderItemId =
        targetProduct?.id ??
        targetProduct?.OrderItemId ??
        targetProduct?.orderItemId ??
        targetProduct?.persistedOrderItemId ??
        null;

      if (!targetOrderItemId) {
        toast.error("This product does not have a saved Order Item to update.");
        return { ok: false, reason: "missing-order-item-id" };
      }

      const response = await updateOrderItems(targetOrderItemId, updatedProduct);
      if (!response) {
        throw new Error("Order Item update did not complete.");
      }

      await fetchOrder?.();
      toast.success("Decoration copied to product");
      return { ok: true, product: updatedProduct };
    } catch (error) {
      console.error("[copy-decoration-to-product] failed", error);
      toast.error(
        error?.response?.data?.message ||
        "Failed to copy decoration to the selected product."
      );
      return { ok: false, error };
    }
  }, [
    cartWorkspace,
    commitWorkspaceMutation,
    decorationCopyProducts,
    showcaseActions,
    fetchOrder,
    isPromoStoreMutationMode,
    isWorkspaceMutationMode,
    onSavePromoStoreProduct,
    onWorkspaceMutationCommitted,
    order?.Products,
    source,
  ]);

  const handleClosePopup = useCallback(() => {
    setOpenPopup(false);
    setSelectedItem(null);
  }, []);

  const handleOpenOtherServicePopUp = useCallback(
    (id = null) => {
      if (!editable && id == null) return;

      setOtherServiceId(id);

      const serviceFromOrder =
        (order?.OtherService || []).find(
          (s) => s?.id === id || s?.cartServiceId === id,
        ) || null;

      setSelectedOtherService(serviceFromOrder);
      setFetchedOtherServiceData(null);
      setOtherServicePopup(true);
    },
    [editable, order?.OtherService],
  );

  const handleOtherServiceDelete = useCallback(
    async (id) => {
      if (!editable) return;

      try {
        if (
          isWorkspaceMutationMode &&
          cartWorkspace?.mutationHandlers?.deleteOtherService
        ) {
          const identity = {
            id,
            cartServiceId: id,
          };
          const mutationResult = await commitWorkspaceMutation({
            source,
            mutationType: "delete-service",
            mutate: () => cartWorkspace.mutationHandlers.deleteOtherService(identity),
            metadata: { identity },
            onWorkspaceMutationCommitted,
          });
          if (!mutationResult?.ok) {
            toast.error("Unable to match the service to delete. Refresh and try again.");
          }
          return;
        }

        await deleteOtherService(id);
        toast.success("Other Service Deleted");
        fetchOrder?.();
      } catch (error) {
        console.error(error);
        toast.error("Failed to delete service");
      }
    },
    [editable, isWorkspaceMutationMode, cartWorkspace, fetchOrder, onWorkspaceMutationCommitted, source],
  );

  const toggleBreakdown = useCallback((itemId) => {
    setExpandedBreakdowns((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 4,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 120,
        tolerance: 8,
      },
    }),
  );

  const transformedData = useMemo(
    () => transformData(order?.Products, order, source),
    [order?.Products, order, source],
  );

  const transformedOtherServiceData = useMemo(
    () => order?.OtherService ?? [],
    [order?.OtherService],
  );
  const cartSectionRows = useMemo(
    () => (order?.cartSections || []).map((section) => ({
      ...section,
      sectionId: section?.sectionId ?? section?.id,
      title: normalizeSectionTitle(section?.title),
      style: normalizeSectionStyle(section?.style ?? section?.Style),
      sortOrder: resolveRenderableSortOrder(section?.sortOrder ?? section?.SortOrder, 0),
      layoutId: `section:${section?.sectionId ?? section?.id}`,
    })),
    [order?.cartSections],
  );

  const cartSummaryProducts = useMemo(
    () => (order?.Products || []).map((product) => shapeProductForCartSummary(product)),
    [order?.Products],
  );

  const orderSummary = useMemo(() => {
    const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
    const asNum = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const getFirstNum = (obj, keys = []) => {
      for (const key of keys) {
        const n = asNum(obj?.[key]);
        if (n != null) return n;
      }
      return null;
    };
    const getTaxRateDecimal = () => {
      const taxOption =
        order?.taxOptions?.find?.((t) => t?.isSelected) ||
        order?.taxOptions?.[0];
      const orderTaxRate =
        asNum(order?.selectedTaxRate) ??
        asNum(order?.SelectedTaxRate) ??
        asNum(order?.TaxRate);
      const optionRate =
        asNum(taxOption?.rate) ??
        asNum(taxOption?.Rate) ??
        asNum(taxOption?.value);
      const raw = orderTaxRate ?? optionRate ?? 0;
      return raw > 1 ? raw / 100 : raw;
    };

    const products = cartSummaryProducts;
    const servicesRaw = order?.OtherService;
    const services = Array.isArray(servicesRaw)
      ? servicesRaw
      : servicesRaw
        ? [servicesRaw]
        : [];

    const transformedTotals = (transformedData || [])
      .map((item) => (item?.data || []).find((r) => r?._isTotal === true))
      .filter(Boolean);

    const productPayloadPreTaxValues = products
      .map((p) =>
        getFirstNum(p, [
          "subtotal",
          "subTotal",
          "Subtotal",
          "total",
          "Total",
          "RetailPrice",
        ]),
      )
      .filter((v) => v != null);

    const productPayloadPreTaxSum = productPayloadPreTaxValues.reduce(
      (sum, v) => sum + v,
      0,
    );
    const hasProductPayloadPreTax =
      productPayloadPreTaxValues.length === products.length &&
      products.length > 0;

      const productsTaxSum = transformedTotals.reduce((sum, row) => {
        const breakdownTax = Array.isArray(row?.taxBreakdown)
          ? row.taxBreakdown.reduce(
              (taxSum, tax) => taxSum + (asNum(tax?.taxAmount) || 0),
              0,
            )
          : null;

        return sum + (breakdownTax != null ? breakdownTax : asNum(row?.taxAmount) || 0);
      }, 0);

    const productsPreTaxFromTransformed = transformedTotals.reduce(
      (sum, row) => {
        const lineTotalInclTax = asNum(row?.amount) || 0;
        const lineTax = asNum(row?.taxAmount) || 0;
        return sum + (lineTotalInclTax - lineTax);
      },
      0,
    );

    const productsPreTaxSum = hasProductPayloadPreTax
      ? productPayloadPreTaxSum
      : productsPreTaxFromTransformed;

    const otherServiceAmounts = sumOtherServiceCustomerAmounts(services, {
      roundServiceTaxPerLine: source === 'sales-order-cart',
    });
    const otherServicePreTaxSum = otherServiceAmounts.subtotal;
    const otherServiceTaxSum = otherServiceAmounts.tax;

    const displaySubtotalRaw = productsPreTaxSum + otherServicePreTaxSum;
    const displayTaxRaw = productsTaxSum + otherServiceTaxSum;

    const subtotalRounded = round2(displaySubtotalRaw);
    const taxRawRounded = round2(displayTaxRaw);
    // SO and invoice-owned carts reconcile their display totals using the
    // same verified tax group. Only a full, unadjusted, uniformly taxable
    // Invoicing Cart may acquire the rounding penny from its saved lines.
    const summaryTax = resolveCartSummaryTaxCents({
      source,
      transformedTotals,
      services,
      subtotalCents: toCents(subtotalRounded),
      rowTaxCents: toCents(taxRawRounded),
      configuredRate: getTaxRateDecimal(),
      adjustments: cartWorkspace?.cartPayload?.Adjustments || order?.Adjustments || [],
      invoiceRole: cartWorkspace?.cartPayload?.meta?.invoiceRole ||
        cartWorkspace?.cartPayload?.meta?.role || '',
    });
    const taxFromSubtotal = fromCents(summaryTax.taxFromSubtotalCents);
    const taxDeltaCents = summaryTax.taxDeltaCents;
    const chosenTax = fromCents(summaryTax.chosenTaxCents);

    const displaySubtotal = subtotalRounded;
    const displayTotal = round2(displaySubtotal + chosenTax);

    const fallbackTotals = computeTotalsFromOrder({
      Products: products,
      OtherService: services,
    });
    const fallbackSubtotal = fromCents(fallbackTotals.subtotalCents);
    const fallbackTax = fromCents(
      fallbackTotals.totalCents - fallbackTotals.subtotalCents,
    );
    const fallbackTotal = fromCents(fallbackTotals.totalCents);

    const totalMarginCentsFromComponents = (() => {
      const componentMarginCents = ({ revenue, netCost, qty }) => {
        const rev = asNum(revenue);
        const cost = asNum(netCost);
        const quantity = Math.round(asNum(qty) ?? 0);

        if (rev == null || cost == null || quantity <= 0) return 0;

        const revenueC = toCents(rev);
        const costC = toCents(cost) * quantity;
        return revenueC - costC;
      };

      const rowRevenue = (row) => {
        const qty = asNum(row?.Quantity) ?? 0;
        const total = asNum(row?.Total);

        if (total != null) return total;

        const unit = asNum(row?.Retail) ?? asNum(row?.ClientPrice);
        if (unit != null) return unit * qty;

        return 0;
      };

      return (
        products.reduce((sum, product) => {
          if (product?.isDeleted) return sum;

          const pricingRows = Array.isArray(product?.pricings)
            ? product.pricings
            : [];
          const pricingMargin = pricingRows.reduce((pSum, row) => {
            return (
              pSum +
              componentMarginCents({
                revenue: rowRevenue(row),
                netCost: row?.NetCost,
                qty: row?.Quantity,
              })
            );
          }, 0);

          const decorationMargin = (Array.isArray(product?.decorations)
            ? product.decorations
            : []
          ).reduce((dSum, dec) => {
            const charges = Array.isArray(dec?.Charge) ? dec.Charge : [];
            return (
              dSum +
              charges.reduce((cSum, charge) => {
                return (
                  cSum +
                  componentMarginCents({
                    revenue: rowRevenue(charge),
                    netCost: charge?.NetCost,
                    qty: charge?.Quantity,
                  })
                );
              }, 0)
            );
          }, 0);

          return sum + pricingMargin + decorationMargin;
        }, 0) +
        services.reduce((sum, svc) => {
          if (svc?.isDeleted) return sum;

          return (
            sum +
            componentMarginCents({
              revenue: asNum(svc?.RetailPrice),
              netCost: svc?.UnitCost,
              qty: svc?.Quantity,
            })
          );
        }, 0)
      );
    })();

    const bookedMarginAmountFromComponents = fromCents(
      totalMarginCentsFromComponents,
    );
    const bookedMarginPctFromComponents =
      displaySubtotal > 0
        ? round2((bookedMarginAmountFromComponents / displaySubtotal) * 100)
        : 0;

    const canUseTransformedSummary = transformedTotals.length > 0;

    if (process.env.NODE_ENV === "development") {
      console.debug("[SelectedPresentation][CartSummaryMath]", {
        productsPreTaxSum: round2(productsPreTaxSum),
        productsTaxSum: round2(productsTaxSum),
        otherServicePreTaxSum: round2(otherServicePreTaxSum),
        otherServiceTaxSum: round2(otherServiceTaxSum),
        subtotalRounded: displaySubtotal,
        taxFromSubtotal,
        taxRounded: taxRawRounded,
        chosenTax,
        inferredUniformRate: summaryTax.inferredRate,
        taxDeltaCents,
        totalRounded: displayTotal,
        bookedMarginAmountFromComponents,
        bookedMarginPctFromComponents,
        fallback: {
          subtotal: round2(fallbackSubtotal),
          tax: round2(fallbackTax),
          total: round2(fallbackTotal),
          bookedMarginAmount: round2(fromCents(fallbackTotals.marginCents)),
          bookedMarginPct: round2(fallbackTotals.marginPct),
        },
        deltasVsFallback: {
          subtotal: round2(displaySubtotal - fallbackSubtotal),
          tax: round2(chosenTax - fallbackTax),
          total: round2(displayTotal - fallbackTotal),
        },
      });
    }

    return {
      bookedMarginAmount: bookedMarginAmountFromComponents,
      bookedMarginPct: bookedMarginPctFromComponents,
      subtotal: canUseTransformedSummary ? displaySubtotal : fallbackSubtotal,
      totalInclTax: canUseTransformedSummary ? displayTotal : fallbackTotal,
    };
  }, [
    cartSummaryProducts,
    order?.OtherService,
    order?.taxOptions,
    order?.selectedTaxRate,
    order?.Adjustments,
    cartWorkspace?.cartPayload?.Adjustments,
    cartWorkspace?.cartPayload?.meta?.invoiceRole,
    cartWorkspace?.cartPayload?.meta?.role,
    transformedData,
    source,
  ]);

  const cartMathDebugPayload = useMemo(
    () => ({
      computedAt: new Date().toISOString(),
      orderSummary,
      transformedData,
      inputs: {
        products: order?.Products || [],
        summaryProducts: cartSummaryProducts,
        otherService: order?.OtherService || [],
      },
    }),
    [orderSummary, transformedData, order?.Products, cartSummaryProducts, order?.OtherService],
  );

  const fetchData = useCallback(async () => {
    try {
      if (isWorkspaceMode) return;

      const response = await getSingleOtherService(otherServiceId);
      setFetchedOtherServiceData(response);
    } catch (error) {
      console.error(error);
    }
  }, [isWorkspaceMode, otherServiceId]);

  useEffect(() => {
    if (otherServicePopup && otherServiceId) fetchData();
  }, [otherServicePopup, otherServiceId, fetchData]);

  const totalItemCount =
    transformedData.length + transformedOtherServiceData.length;
  const isEmpty = totalItemCount === 0;
  const isWorkspaceLoading =
    Boolean(cartWorkspace?.loading) || refreshingOrderCart || savingOrderCartItem;
  const productLayoutRows = useMemo(
    () => buildProductLayoutRows(transformedData),
    [transformedData],
  );
  const cartLayoutRows = useMemo(
    () => buildCartLayout({ products: productLayoutRows, sections: cartSectionRows }),
    [productLayoutRows, cartSectionRows],
  );
  const dragDisabled = !editable || transformedData.length < 2;
  const layoutDragDisabled = !!showcaseActions || !editable || capabilities.reorder === false || cartLayoutRows.length < 2;

  const openAddSectionDialog = useCallback(() => {
    if (!editable) return;
    setSectionDialogMode("add");
    setSectionTitleDraft("");
    setSectionStyleDraft("light");
    setSelectedSection(null);
    setSectionDialogOpen(true);
  }, [editable]);

  const openEditSectionDialog = useCallback((section) => {
    if (!editable) return;
    setSectionDialogMode("edit");
    setSectionTitleDraft(normalizeSectionTitle(section?.title));
    setSectionStyleDraft(normalizeSectionStyle(section?.style ?? section?.Style));
    setSelectedSection(section);
    setSectionDialogOpen(true);
  }, [editable]);

  const closeSectionDialog = useCallback(() => {
    setSectionDialogOpen(false);
    setSelectedSection(null);
    setSectionTitleDraft("");
    setSectionStyleDraft("light");
  }, []);

  const handleSaveSection = useCallback(async () => {
    const normalizedTitle = normalizeSectionTitle(sectionTitleDraft);
    if (!normalizedTitle) {
      toast.error("Section title is required.");
      return;
    }
    if (normalizedTitle.length > 200) {
      toast.error("Section title must be 200 characters or fewer.");
      return;
    }
    const normalizedStyle = normalizeSectionStyle(sectionStyleDraft);

    try {
      if (isWorkspaceMutationMode) {
        const mutationType = sectionDialogMode === "add" ? "add-section" : "edit-section";
        const mutationResult = await commitWorkspaceMutation({
          source,
          mutationType,
          mutate: () => sectionDialogMode === "add"
            ? cartWorkspace?.mutationHandlers?.addSection?.(normalizedTitle, normalizedStyle)
            : cartWorkspace?.mutationHandlers?.editSection?.(selectedSection, normalizedTitle, normalizedStyle),
          metadata: {
            section: selectedSection,
            title: normalizedTitle,
            style: normalizedStyle,
          },
          onWorkspaceMutationCommitted,
        });
        if (!mutationResult?.ok) {
          toast.error("Unable to save the section. Refresh and try again.");
          return;
        }
      } else if (sectionDialogMode === "add") {
        await createOrderCartSection(orderId, normalizedTitle, normalizedStyle);
        await fetchOrder?.({ suppressLoader: true });
      } else {
        const sectionId = selectedSection?.sectionId ?? selectedSection?.id;
        await updateOrderCartSection(orderId, sectionId, normalizedTitle, normalizedStyle);
        await fetchOrder?.({ suppressLoader: true });
      }

      closeSectionDialog();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to save section title.");
    }
  }, [cartWorkspace, closeSectionDialog, fetchOrder, isWorkspaceMutationMode, onWorkspaceMutationCommitted, orderId, sectionDialogMode, sectionStyleDraft, sectionTitleDraft, selectedSection, source]);

  const handleDeleteSection = useCallback(async (section) => {
    if (!editable) return;
    if (!window.confirm("Are you sure you want to delete this section title?")) return;

    try {
      if (isWorkspaceMutationMode) {
        const mutationResult = await commitWorkspaceMutation({
          source,
          mutationType: "delete-section",
          mutate: () => cartWorkspace?.mutationHandlers?.deleteSection?.(section),
          metadata: { section },
          onWorkspaceMutationCommitted,
        });
        if (!mutationResult?.ok) {
          toast.error("Unable to delete the section. Refresh and try again.");
        }
        return;
      }

      const sectionId = section?.sectionId ?? section?.id;
      await deleteOrderCartSection(orderId, sectionId);
      await fetchOrder?.({ suppressLoader: true });
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to delete section title.");
    }
  }, [cartWorkspace, editable, fetchOrder, isWorkspaceMutationMode, onWorkspaceMutationCommitted, orderId, source]);

  const handleDragEnd = useCallback(
    async ({ active, over }) => {
      if (layoutDragDisabled) return;
      if (!active?.id || !over?.id || active.id === over.id) return;

      const activeRow = cartLayoutRows.find((row) => row.layoutId === active.id);
      const overRow = cartLayoutRows.find((row) => row.layoutId === over.id);

      if (!activeRow || !overRow) return;

      if (
        isWorkspaceMutationMode &&
        cartWorkspace?.mutationHandlers?.reorderLayout
      ) {
        const mutationType =
          activeRow.type === CART_LAYOUT_ROW_TYPES.PRODUCT &&
          overRow.type === CART_LAYOUT_ROW_TYPES.PRODUCT
            ? "reorder-products"
            : "reorder-layout";
        const mutationResult = await commitWorkspaceMutation({
          source,
          mutationType,
          mutate: () => cartWorkspace.mutationHandlers.reorderLayout(activeRow.layoutId, overRow.layoutId),
          metadata: {
            activeIdentity: activeRow.layoutId,
            overIdentity: overRow.layoutId,
          },
          onWorkspaceMutationCommitted,
        });
        if (!mutationResult?.ok) {
          toast.error("Unable to reorder the cart. Refresh and try again.");
        }
        return;
      }

      if (typeof onReorderLayout === "function") {
        await onReorderLayout(activeRow.layoutId, overRow.layoutId);
      } else if (
        activeRow.type === CART_LAYOUT_ROW_TYPES.PRODUCT &&
        overRow.type === CART_LAYOUT_ROW_TYPES.PRODUCT &&
        typeof onReorderProducts === "function"
      ) {
        await onReorderProducts(
          getProductWorkspaceIdentity(activeRow),
          getProductWorkspaceIdentity(overRow),
        );
      }
    },
    [
      cartLayoutRows,
      cartWorkspace,
      getProductWorkspaceIdentity,
      isWorkspaceMutationMode,
      layoutDragDisabled,
      onWorkspaceMutationCommitted,
      onReorderLayout,
      onReorderProducts,
      source,
    ],
  );

  return (
    <>
      {enableToastContainer && <ToastContainer />}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          position: "relative",
          minHeight: isWorkspaceLoading ? 220 : undefined,
        }}
      >
        {isWorkspaceLoading && (
          <Box
            aria-live="polite"
            aria-label={
              source === "order-cart"
                ? "Saving or refreshing Order Cart"
                : source === "sales-order-cart"
                  ? "Loading Sales Order Cart"
                  : "Loading Invoicing Cart"
            }
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.74),
              backdropFilter: "blur(1px)",
              borderRadius: 1,
              cursor: "wait",
            }}
          >
            <Stack alignItems="center" spacing={1}>
              <CircularProgress size={26} thickness={4} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: "text.secondary" }}
              >
                {source === "order-cart"
                  ? (savingOrderCartItem ? "Saving Order Cart..." : "Refreshing Order Cart...")
                  : source === "sales-order-cart"
                    ? "Loading Sales Order Cart..."
                    : "Loading Invoicing Cart..."}
              </Typography>
            </Stack>
          </Box>
        )}
        {isWorkspaceMode && source === "sales-order-cart" && !isWorkspaceLoading && cartWorkspace?.error && (
          <Alert severity="error" role="alert" sx={{ mx: 0.5 }}>
            Sales Order Cart could not load its approved snapshot. {cartWorkspace.error}
          </Alert>
        )}
        {!showcaseActions && <Box
          sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, px: 0.5 }}
        >
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleShowProductsPopup}
            disabled={!canAddProduct}
            sx={{
              textTransform: "none",
              borderRadius: 1,
              fontWeight: 700,
              boxShadow: "none",
              minHeight: 36,
            }}
          >
            Add Product
          </Button>

          <Tooltip title={showcaseActions ? "Title rows are unavailable for Showcase." : ''} describeChild arrow>
            <span tabIndex={showcaseActions ? 0 : undefined} aria-label={showcaseActions ? "Title rows are unavailable for Showcase." : undefined}>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            startIcon={<AddIcon />}
            onClick={onSecondaryAction || openAddSectionDialog}
            disabled={!canEditSections}
            sx={{
              textTransform: "none",
              borderRadius: 1,
              fontWeight: 700,
              minHeight: 36,
            }}
          >
            {secondaryActionLabel}
          </Button>
            </span>
          </Tooltip>

          <Tooltip title={showcaseActions ? "Service items are unavailable for Showcase." : ''} describeChild arrow>
            <span tabIndex={showcaseActions ? 0 : undefined} aria-label={showcaseActions ? "Service items are unavailable for Showcase." : undefined}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenOtherServicePopUp(null)}
            disabled={!canAddService}
            sx={{
              textTransform: "none",
              borderRadius: 1,
              fontWeight: 700,
              minHeight: 36,
            }}
          >
            Add Other Service
          </Button>
            </span>
          </Tooltip>

          {source === "invoicing-cart" && typeof onAddAdjustment === "function" && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={onAddAdjustment}
              disabled={!editable || capabilities.adjustments !== true}
              sx={{
                textTransform: "none",
                borderRadius: 1,
                fontWeight: 700,
                minHeight: 36,
              }}
            >
              Add Adjustment
            </Button>
          )}

          {typeof onOpenCartHistory === "function" && (
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<HistoryIcon />}
              onClick={onOpenCartHistory}
              sx={{
                textTransform: "none",
                borderRadius: 1,
                fontWeight: 600,
                minHeight: 36,
                color: "text.secondary",
              }}
            >
              Cart History
            </Button>
          )}

          {isWorkspaceMode && source !== "order-cart" && (
            <>
              <Chip
                size="small"
                label={
                  source === "sales-order-cart"
                    ? `Sales Order Cart (${salesOrderCartUnlocked ? "Editing" : "Read-only"})`
                    : "Invoicing Cart"
                }
                color={source === "sales-order-cart" && salesOrderCartUnlocked ? "warning" : "default"}
              />

              {source === "sales-order-cart" && !salesOrderCartUnlocked && typeof onUnlockSalesOrderCart === "function" && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<LockOutlinedIcon />}
                  onClick={() => onUnlockSalesOrderCart?.()}
                  sx={{
                    textTransform: "none",
                    borderRadius: 1,
                    fontWeight: 600,
                    minHeight: 32,
                  }}
                >
                  Unlock
                </Button>
              )}
            </>
          )}

          {isWorkspaceMode && source === "invoicing-cart" && (
            <>
              {cartWorkspace?.dirty ? (
                <Chip
                  size="small"
                  color="warning"
                  label="Unsaved invoicing cart changes"
                />
              ) : editable ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label="No unsaved changes"
                />
              ) : (
                <Chip
                  size="small"
                  color="default"
                  label={
                    cartWorkspace?.editReason || "Invoicing cart is read-only"
                  }
                />
              )}

              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleSaveWorkspaceCart}
                disabled={
                  !editable ||
                  !cartWorkspace?.dirty ||
                  savingWorkspaceCart ||
                  cartWorkspace?.loading
                }
              >
                {savingWorkspaceCart ? "Saving..." : "Save Invoicing Cart"}
              </Button>
            </>
          )}

          <Box sx={{ flex: 1 }} />

          {!isEmpty && !showcaseActions && (
            <Chip
              size="small"
              label={`${totalItemCount} item${
                totalItemCount !== 1 ? "s" : ""
              }`}
              sx={{
                height: 26,
                fontWeight: 600,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                color: "primary.main",
                borderRadius: 1,
              }}
            />
          )}
        </Box>}

        {renderShowcaseToolbar?.({ view: productView, onViewChange: selectProductView })}

        {isEmpty && (
          <Paper
            elevation={0}
            sx={{
              border: (theme) =>
                `1px dashed ${alpha(theme.palette.divider, 1)}`,
              borderRadius: 1,
              bgcolor: "transparent",
            }}
          >
            <EmptyCartState
              showcase={!!showcaseActions}
              onAddProduct={handleShowProductsPopup}
              onAddService={() => handleOpenOtherServicePopUp(null)}
              canAddProduct={canAddProduct}
              canAddService={canAddService}
            />
          </Paper>
        )}

        {showcaseProductViewScope && !renderShowcaseToolbar && <ShowcaseProductViewSelector view={productView} onChange={selectProductView} orderingAvailable={!layoutDragDisabled} />}

        {cartLayoutRows.length > 0 && productView !== "list" && (
          <ShowcaseProductLayout rows={cartLayoutRows} view={productView}
            renderSection={(row) => <SectionRow row={row} editable={canEditSections} dragDisabled
              onEdit={openEditSectionDialog} onDelete={handleDeleteSection} />}
            renderProduct={(row, index) => <ProductCard row={row} index={index} view={productView}
              onEdit={handleEditClick} onDelete={handleDeleteClick} onCopy={handleCopyClick}
              onViewDetails={showcaseActions ? viewShowcaseDetails : null}
              editable={editable} capabilities={capabilities} actionRestrictions={showcaseRestrictions(row)}
              deleteProtectionEnabled={deleteProtectionEnabled} protectedOrderItemIds={protectedOrderItemIds} />}
          />
        )}

        {cartLayoutRows.length > 0 && productView === "list" && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={cartLayoutRows.map((row) => row.layoutId).filter(Boolean)}
              strategy={verticalListSortingStrategy}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {cartLayoutRows.map((row, index) => (
                  row.type === CART_LAYOUT_ROW_TYPES.SECTION ? (
                    <SortableSectionRow
                      key={row.layoutId ?? index}
                      row={row}
                      editable={canEditSections}
                      dragDisabled={layoutDragDisabled}
                      onEdit={openEditSectionDialog}
                      onDelete={handleDeleteSection}
                    />
                  ) : (
                    <SortableProductCard
                      key={row.layoutId ?? row.itemId ?? index}
                      row={row}
                      index={index}
                      actionRestrictions={showcaseRestrictions(row)}
                      accessibleBreakdown={!!showcaseProductViewScope}
                      expanded={!!expandedBreakdowns[row.itemId]}
                      onToggleBreakdown={toggleBreakdown}
                      onEdit={handleEditClick}
                      onViewDetails={showcaseActions ? viewShowcaseDetails : null}
                      onDelete={handleDeleteClick}
                      onCopy={handleCopyClick}
                      editable={editable}
                      capabilities={capabilities}
                      deleteProtectionEnabled={deleteProtectionEnabled}
                      protectedOrderItemIds={protectedOrderItemIds}
                      dragDisabled={layoutDragDisabled}
                    />
                  )
                ))}
              </Box>
            </SortableContext>
          </DndContext>
        )}

        {transformedOtherServiceData.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Divider sx={{ my: 0.5 }} />
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ px: 0.5 }}
            >
              <MiscellaneousServicesOutlinedIcon
                sx={{ fontSize: 16, color: "text.secondary" }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  fontSize: "0.82rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Other Services
              </Typography>
              <Chip
                size="small"
                label={transformedOtherServiceData.length}
                sx={{
                  height: 20,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  bgcolor: "rgba(13,94,166,0.1)",
                  color: "#0D5EA6",
                  "& .MuiChip-label": { px: 0.8 },
                }}
              />
            </Stack>
            {transformedOtherServiceData.map((row, index) => (
              <OtherServiceCard
                key={row.id ?? row.cartServiceId ?? index}
                row={row}
                onEdit={(r) =>
                  handleOpenOtherServicePopUp(r.id ?? r.cartServiceId)
                }
                onDelete={handleOtherServiceDelete}
                editable={editable}
              />
            ))}
          </Box>
        )}

        {showcaseFooter}

        {!isEmpty && (
          <CartSummaryBar
            summaryLabel={showcaseActions ? "Page totals" : undefined}
            orderSummary={orderSummary}
            onDebugOpen={() => setOpenCartMathDebug(true)}
          />
        )}
      </Box>

      {showcaseDetailsProduct && <ProductPopup readOnly isOpen
        product={showcaseDetailsProduct}
        productId={parseOrderProductId(showcaseDetailsProduct.ProductId).id || showcaseDetailsProduct.Id || showcaseDetailsProduct.id}
        lookupType={String(showcaseDetailsProduct.Source || 'promoworld').toLowerCase()}
        onRequestClose={() => setShowcaseDetailsProduct(null)} />}

      {openPopup && (
        <EditProduct
          open={openPopup}
          initialItem={selectedItem}
          onClose={handleClosePopup}
          orderItemId={orderItemId}
          order={order}
          fetchOrder={trackedFetchOrder}
          onSaveLifecycleChange={setSavingOrderCartItem}
          isPromoStore={isPromoStore}
          controlledMode={shouldUseControlledEdit}
          source={source}
          promoStoreCategories={promoStoreCategories}
          canSeeManualPricingToggle={canSeeManualPricingToggle}
          cartProducts={decorationCopyProducts}
          onCopyDecorationToProduct={handleCopyDecorationToProduct}
          onSaveProduct={async (updatedProduct) => {
            if (showcaseActions) {
              await showcaseActions.mutate('edit', selectedItem.showcaseIdentity.key, showcaseEditedProduct(selectedItem, updatedProduct));
              return;
            }
            if (
              isWorkspaceMutationMode &&
              cartWorkspace?.mutationHandlers?.editProduct
            ) {
              const originalProduct = selectedItem || updatedProduct;
              const savedProduct = await recoverMissingCustomSupplierOnSalesOrderEdit({
                source,
                originalProduct,
                updatedProduct,
                order,
                orderId,
                repair: rehydrateMissingCustomOrderItemSupplier,
              });

              const mutationResult = await commitWorkspaceMutation({
                source,
                mutationType: "edit-product",
                mutate: () => cartWorkspace.mutationHandlers.editProduct(
                  originalProduct,
                  savedProduct,
                ),
                metadata: { originalProduct, updatedProduct: savedProduct },
                onWorkspaceMutationCommitted,
              });
              if (!mutationResult?.ok) {
                toast.error("Unable to match the edited item in this cart. Refresh and try again.");
                return;
              }
              return;
            }

            if (isPromoStoreMutationMode) {
              const products = Array.isArray(order?.Products) ? order.Products : [];

              const samePromoStoreLine = (product) => {
                if (!product || !selectedItem) return false;

                if (
                  selectedItem.cartLineId != null &&
                  product.cartLineId != null &&
                  String(product.cartLineId) === String(selectedItem.cartLineId)
                ) {
                  return true;
                }

                if (
                  selectedItem.id != null &&
                  product.id != null &&
                  String(product.id) === String(selectedItem.id)
                ) {
                  return true;
                }

                return false;
              };

              const normalizePromoStoreEditedProduct = (product = {}, nextProduct = {}) => {
                const persistedId =
                  product.id ??
                  product.PromoStoreItemId ??
                  product.promoStoreItemId ??
                  product.itemId ??
                  nextProduct.id ??
                  nextProduct.PromoStoreItemId ??
                  nextProduct.promoStoreItemId ??
                  nextProduct.itemId;

                const nextPricings =
                  nextProduct.Pricings ??
                  nextProduct.pricings ??
                  product.Pricings ??
                  product.pricings ??
                  [];

                const nextDecorations =
                  nextProduct.Decorations ??
                  nextProduct.decorations ??
                  product.Decorations ??
                  product.decorations ??
                  [];
                const normalizedImages = normalizePromoStoreImageSelection({
                  selectedImage:
                    nextProduct.ImageUrl ??
                    nextProduct.imageUrl ??
                    product.ImageUrl ??
                    product.imageUrl,
                  imageUrls:
                    nextProduct.ImageUrls ??
                    nextProduct.imageUrls ??
                    product.ImageUrls ??
                    product.imageUrls,
                });

                return {
                  ...product,
                  ...nextProduct,
                  id: persistedId,
                  PromoStoreItemId: persistedId,
                  promoStoreItemId: persistedId,
                  itemId: persistedId,
                  cartLineId: product.cartLineId ?? nextProduct.cartLineId,
                  Source: product.Source ?? nextProduct.Source,
                  categoryIds:
                    nextProduct.categoryIds ??
                    nextProduct.CategoryIds ??
                    product.categoryIds ??
                    product.CategoryIds ??
                    [],
                  categories:
                    nextProduct.categories ??
                    nextProduct.Categories ??
                    product.categories ??
                    product.Categories ??
                    [],
                  ImageUrl: normalizedImages.selectedImage,
                  imageUrl: normalizedImages.selectedImage,
                  ImageUrls: normalizedImages.imageUrls,
                  imageUrls: normalizedImages.imageUrls,
                  Pricings: Array.isArray(nextPricings) ? nextPricings : [],
                  pricings: Array.isArray(nextPricings) ? nextPricings : [],
                  Decorations: Array.isArray(nextDecorations) ? nextDecorations : [],
                  decorations: Array.isArray(nextDecorations) ? nextDecorations : [],
                };
              };

              const nextProducts = products.map((product) =>
                samePromoStoreLine(product)
                  ? normalizePromoStoreEditedProduct(product, updatedProduct)
                  : product
              );

              await onSavePromoStoreProduct(nextProducts);
            }
          }}
        />
      )}


      <Dialog open={sectionDialogOpen} onClose={closeSectionDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {sectionDialogMode === "add" ? "Add Section Title" : "Edit Section Title"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Section title"
            value={sectionTitleDraft}
            inputProps={{ maxLength: 200 }}
            onChange={(event) => setSectionTitleDraft(event.target.value)}
          />
          <TextField
            select
            fullWidth
            margin="dense"
            label="Style"
            value={normalizeSectionStyle(sectionStyleDraft)}
            onChange={(event) => setSectionStyleDraft(event.target.value)}
          >
            <MenuItem value="light">Light</MenuItem>
            <MenuItem value="dark">Dark</MenuItem>
          </TextField>
        </DialogContent>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 3, pb: 2 }}>
          <Button onClick={closeSectionDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSection}>Save</Button>
        </Box>
      </Dialog>

      {otherServicePopup && (
        <OtherServiceModal
          orderId={order?.OrderId}
          open={otherServicePopup}
          fetchOrder={fetchOrder}
          onClose={() => setOtherServicePopup(false)}
          fetchedOtherServiceData={fetchedOtherServiceData}
          otherServiceId={otherServiceId}
          controlledMode={isWorkspaceMutationMode}
          initialService={selectedOtherService}
          source={source}
          onSaveService={async (servicePayload) => {
            if (!isWorkspaceMutationMode) return;

            const hasIdentity = Boolean(
              selectedOtherService?.id || selectedOtherService?.cartServiceId,
            );

            if (
              hasIdentity &&
              cartWorkspace?.mutationHandlers?.editOtherService
            ) {
              const mutationResult = await commitWorkspaceMutation({
                source,
                mutationType: "edit-service",
                mutate: () => cartWorkspace.mutationHandlers.editOtherService(
                  selectedOtherService,
                  servicePayload,
                ),
                metadata: { service: servicePayload },
                onWorkspaceMutationCommitted,
              });
              if (!mutationResult?.ok) {
                toast.error("Unable to match the service to edit. Refresh and try again.");
              }
              return;
            }

            if (cartWorkspace?.mutationHandlers?.addOtherService) {
              const mutationResult = await commitWorkspaceMutation({
                source,
                mutationType: "add-service",
                mutate: () => cartWorkspace.mutationHandlers.addOtherService(servicePayload),
                metadata: { service: servicePayload },
                onWorkspaceMutationCommitted,
              });
              if (!mutationResult?.ok) {
                toast.error("Unable to add the service. Refresh and try again.");
              }
            }
          }}
        />
      )}

      <Dialog
        open={openCartMathDebug}
        onClose={() => setOpenCartMathDebug(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Cart Summary Debug Payload</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              borderRadius: 1,
              bgcolor: "#0f172a",
              color: "#e2e8f0",
              fontSize: "0.78rem",
              lineHeight: 1.45,
              overflow: "auto",
              maxHeight: "70vh",
            }}
          >
            {JSON.stringify(cartMathDebugPayload, null, 2)}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SelectedProducts;
